import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  GraduationCap,
  Plus,
  FileText,
  Users,
  AlertTriangle,
  Clock,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  LogOut,
  Loader2,
  BookOpen,
  UserCheck,
  UserX,
  CheckCircle,
  XCircle,
  History,
  Key,
  Copy,
  Download,
  BarChart3,
  KeyRound,
  Bell,
  Filter,
  RefreshCw,
  LinkIcon,
  Share2,
  Send,
  Lock,
  Unlock,
  FileSpreadsheet,
  Settings,
  Layers,
  Folder,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Banknote,
  ArrowLeft as ArrowLeftIcon,
  ArrowRight as ArrowRightIcon,
  ArrowUp,
  Home,
  Search,
  LayoutGrid,
  List as ListIcon,
  MoreHorizontal,
  Star,
  Newspaper,
  Video,
  MessageCircle,
  Radio,
  ToggleLeft,
  ToggleRight,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import NotificationCenter from '../components/NotificationCenter';
import BankAccessManager from '../components/BankAccessManager';
import TeacherVideoManager from '../components/TeacherVideoManager';
import TeacherMessages from '../components/TeacherMessages';
import TeacherWallet from '../components/TeacherWallet';
import TeacherHelpers from '../components/TeacherHelpers';
import TopicBankAnalytics from '../components/TopicBankAnalytics';
import FloatingChat from '../components/FloatingChat';

// Grade/section lists for the folder-settings dialog (الصف على مستوى المجلد).
const FOLDER_GRADES = [
  'الصف الأول', 'الصف الثاني', 'الصف الثالث', 'الصف الرابع', 'الصف الخامس',
  'الصف السادس', 'الصف السابع', 'الصف الثامن', 'الصف التاسع', 'الصف العاشر',
  'الصف الحادي عشر', 'الصف الثاني عشر', 'غير ذلك',
];
const FOLDER_SECTIONS = ['أ', 'ب', 'ج', 'د', 'هـ', 'و'];

// Dashboard tab → the permission a helper (assistant) teacher needs to see it.
const TAB_PERMS = {
  exams: 'exams', 'question-bank': 'question_bank', videos: 'videos', wallet: 'wallet',
  students: 'students', violations: 'exams', news: 'news',
};

// Format a duration (seconds) as Arabic h/m/s — used for "time on platform".
const fmtDuration = (secs) => {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  if (s < 60) return `${s} ث`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} د`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h} س ${rm} د` : `${h} س`;
};

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { teacher, token, logout, isTeacher } = useAuth();
  // A helper teacher works on the owner's account with only the allowed sections.
  const isHelper = Boolean(teacher?.is_helper);
  const [helperPerms, setHelperPerms] = useState(teacher?.permissions || []);
  const can = (perm) => !isHelper || helperPerms.includes(perm);
  const [exams, setExams] = useState([]);
  const [violations, setViolations] = useState([]);
  const [pendingStudents, setPendingStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [passwordResets, setPasswordResets] = useState([]);
  const [examAccessCodes, setExamAccessCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('exams');
  useEffect(() => {
    if (!isHelper || helperPerms.includes(TAB_PERMS[activeTab])) return;
    const first = Object.keys(TAB_PERMS).find((t) => helperPerms.includes(TAB_PERMS[t]));
    if (first) setActiveTab(first);
  }, [isHelper, helperPerms, activeTab]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [processingStudent, setProcessingStudent] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(null);
  const [codeValidityMinutes, setCodeValidityMinutes] = useState(60);
  const [deleteStudentDialogOpen, setDeleteStudentDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(false);
  const [teacherCode, setTeacherCode] = useState('');
  const [releasingResults, setReleasingResults] = useState(null);
  
  // Secret Question State
  const [secretQuestionDialogOpen, setSecretQuestionDialogOpen] = useState(false);
  const [secretQuestion, setSecretQuestion] = useState('');
  const [secretAnswer, setSecretAnswer] = useState('');
  const [hasSecretQuestion, setHasSecretQuestion] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);

  // Recovery Codes State
  const [recoveryCodesDialogOpen, setRecoveryCodesDialogOpen] = useState(false);
  const [recoveryCodesRemaining, setRecoveryCodesRemaining] = useState(0);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  
  // Violation filters
  const [violationFilters, setViolationFilters] = useState({
    examId: 'all',
    studentId: 'all',
    violationType: 'all'
  });
  const [filteredViolations, setFilteredViolations] = useState([]);
  const [violationTypes, setViolationTypes] = useState([]);
  const [violationSummary, setViolationSummary] = useState([]);
  const [resetViolationsDialogOpen, setResetViolationsDialogOpen] = useState(false);
  const [resettingViolations, setResettingViolations] = useState(false);

  // Export-to-bank state
  const [exportBankOpen, setExportBankOpen] = useState(false);
  const [exportBankExam, setExportBankExam] = useState(null);
  const [exportBankSelectedIds, setExportBankSelectedIds] = useState({});
  const [exportBankTopicId, setExportBankTopicId] = useState('');
  const [exportingToBank, setExportingToBank] = useState(false);

  // Filters
  const [gradeFilter, setGradeFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [studentSearch, setStudentSearch] = useState('');

  // Visibility toggle dialog state
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);
  const [selectedExamForVisibility, setSelectedExamForVisibility] = useState(null);
  const [visibilitySettings, setVisibilitySettings] = useState({});
  const [savingVisibility, setSavingVisibility] = useState(false);

  // Edit student dialog state
  const [editStudentDialogOpen, setEditStudentDialogOpen] = useState(false);
  const [studentToEdit, setStudentToEdit] = useState(null);
  const [editStudentForm, setEditStudentForm] = useState({});
  const [savingStudent, setSavingStudent] = useState(false);

  // Question Bank (topics) state
  const [topics, setTopics] = useState([]);
  const [topicDialogOpen, setTopicDialogOpen] = useState(false);
  // A bank file only carries title/description and the folder it lives in.
  // All exam settings (grade, distribution, count, time, price, tries…) live on
  // the FOLDER — set them from «إعدادات المجلد».
  const [topicForm, setTopicForm] = useState({
    title: '',
    description: '',
    category: '',
  });
  const [savingTopic, setSavingTopic] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState(null);
  const [deleteTopicDialogOpen, setDeleteTopicDialogOpen] = useState(false);

  // News ticker state
  const [newsItems, setNewsItems] = useState([]);
  const [newsForm, setNewsForm] = useState({ content: '', grade: 'all', is_active: true });
  const [savingNews, setSavingNews] = useState(false);
  const [editingNewsId, setEditingNewsId] = useState(null);

  // DM unread badge
  const [dmUnread, setDmUnread] = useState(0);
  const [onlineStudentIds, setOnlineStudentIds] = useState(new Set());
  const [showBankAnalytics, setShowBankAnalytics] = useState(false);

  // Videos (for the paid-access manager: lets the teacher price/unlock videos too)
  const [bankVideos, setBankVideos] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const FRONTEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

  const grades = [
    'الصف الأول', 'الصف الثاني', 'الصف الثالث', 'الصف الرابع',
    'الصف الخامس', 'الصف السادس', 'الصف السابع', 'الصف الثامن',
    'الصف التاسع', 'الصف العاشر', 'الصف الحادي عشر', 'الصف الثاني عشر',
    'غير ذلك',
  ];
  const sections = ['أ', 'ب', 'ج', 'د', 'هـ', 'و'];

  useEffect(() => {
    if (!isTeacher) {
      navigate('/teacher/login');
      return;
    }
    fetchData();
  }, [isTeacher, navigate]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      can('exams') && fetchExams(),
      can('exams') && fetchViolations(),
      can('students') && fetchPendingStudents(),
      can('students') && fetchAllStudents(),
      can('students') && fetchPasswordResets(),
      fetchTeacherCode(),
      !isHelper && fetchSecretQuestion(),
      !isHelper && fetchRecoveryCodesStatus(),
      can('question_bank') && fetchTopics(),
      can('news') && fetchNews(),
    ]);
    setLoading(false);
  };

  const fetchNews = async () => {
    try {
      const r = await fetch(`${API_URL}/api/teacher/news`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) setNewsItems(await r.json());
    } catch { /* ignore */ }
  };

  const handleSaveNews = async () => {
    if (!newsForm.content.trim()) { toast.error('يرجى كتابة محتوى الخبر'); return; }
    setSavingNews(true);
    try {
      if (editingNewsId) {
        const r = await fetch(`${API_URL}/api/teacher/news/${editingNewsId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(newsForm),
        });
        if (r.ok) {
          const updated = await r.json();
          setNewsItems(prev => prev.map(n => n.id === editingNewsId ? updated : n));
          toast.success('تم تحديث الخبر');
          setEditingNewsId(null);
          setNewsForm({ content: '', grade: 'all', is_active: true });
        }
      } else {
        const r = await fetch(`${API_URL}/api/teacher/news`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(newsForm),
        });
        if (r.ok) {
          const created = await r.json();
          setNewsItems(prev => [created, ...prev]);
          toast.success('تم إضافة الخبر');
          setNewsForm({ content: '', grade: 'all', is_active: true });
        }
      }
    } catch { toast.error('حدث خطأ'); }
    setSavingNews(false);
  };

  const handleToggleNews = async (item) => {
    try {
      const r = await fetch(`${API_URL}/api/teacher/news/${item.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !item.is_active }),
      });
      if (r.ok) {
        const updated = await r.json();
        setNewsItems(prev => prev.map(n => n.id === item.id ? updated : n));
      }
    } catch { toast.error('حدث خطأ'); }
  };

  const handleDeleteNews = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/teacher/news/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        setNewsItems(prev => prev.filter(n => n.id !== id));
        toast.success('تم حذف الخبر');
      }
    } catch { toast.error('حدث خطأ'); }
  };

  const fetchTopics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/topics`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTopics(data);
      }
    } catch (error) {
      console.error('Error fetching topics:', error);
    }
  };

  const [allTopicAttempts, setAllTopicAttempts] = useState([]);
  const fetchAllTopicAttempts = async () => {
    try {
      const r = await fetch(`${API_URL}/api/teacher/export/all-topic-results`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setAllTopicAttempts(data.results || []);
      }
    } catch {/* ignore */}
  };
  useEffect(() => { if (token && can('question_bank')) fetchAllTopicAttempts(); }, [token]); // eslint-disable-line

  // Poll DM unread count for the Messages tab badge
  useEffect(() => {
    if (!token || !can('chat')) return;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/teacher/messages/unread-total`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setDmUnread((await r.json()).unread || 0);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [token, API_URL]);

  // Poll currently-online students (presence)
  useEffect(() => {
    if (!token || !can('students')) return;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/teacher/online-students`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setOnlineStudentIds(new Set((d.students || []).map((s) => s.student_id)));
        }
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [token, API_URL]);

  // Load videos so the paid-access manager can price/unlock them for students
  useEffect(() => {
    if (!token || !can('videos')) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/videos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setBankVideos(await r.json());
      } catch { /* ignore */ }
    })();
  }, [token, API_URL]);

  const resetTopicForm = () => setTopicForm({ title: '', description: '', category: '' });

  const handleCreateTopic = async () => {
    if (!topicForm.title.trim()) {
      toast.error('يرجى إدخال عنوان الموضوع');
      return;
    }
    setSavingTopic(true);
    try {
      const response = await fetch(`${API_URL}/api/topics`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        // Only identity + folder. Exam settings are configured on the folder.
        body: JSON.stringify({
          title: topicForm.title.trim(),
          description: topicForm.description || '',
          category: topicForm.category || '',
        }),
      });
      if (response.ok) {
        const created = await response.json();
        toast.success('تم إنشاء الموضوع بنجاح');
        setTopicDialogOpen(false);
        resetTopicForm();
        await fetchTopics();
        navigate(`/teacher/topics/${created.id}`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'فشل إنشاء الموضوع');
      }
    } catch (error) {
      console.error(error);
      toast.error('حدث خطأ أثناء الإنشاء');
    }
    setSavingTopic(false);
  };

  const handleDeleteTopic = async () => {
    if (!topicToDelete) return;
    try {
      const response = await fetch(`${API_URL}/api/topics/${topicToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('تم حذف الموضوع');
        await fetchTopics();
      } else {
        toast.error('فشل الحذف');
      }
    } catch {
      toast.error('حدث خطأ أثناء الحذف');
    }
    setDeleteTopicDialogOpen(false);
    setTopicToDelete(null);
  };

  const fetchTeacherCode = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/teacher/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTeacherCode(data.teacher_code || '');
        if (data.is_helper) setHelperPerms(data.permissions || []);
      }
    } catch (error) {
      console.error('Error fetching teacher code:', error);
    }
  };

  const fetchSecretQuestion = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/secret-question`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setHasSecretQuestion(data.has_secret_question);
        setSecretQuestion(data.secret_question || '');
      }
    } catch (error) {
      console.error('Error fetching secret question:', error);
    }
  };

  const fetchRecoveryCodesStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teacher/recovery-codes/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRecoveryCodesRemaining(data.remaining_count);
      }
    } catch (e) {
      console.error('Error fetching recovery codes status:', e);
    }
  };

  const handleGenerateRecoveryCodes = async () => {
    setGeneratingCodes(true);
    try {
      const res = await fetch(`${API_URL}/api/teacher/recovery-codes/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGeneratedCodes(data.codes);
        setRecoveryCodesRemaining(data.codes.length);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'فشل توليد رموز الاستعادة');
      }
    } catch (e) {
      toast.error('حدث خطأ أثناء التوليد');
    }
    setGeneratingCodes(false);
  };

  const handleSaveSecretQuestion = async () => {
    if (!secretQuestion || !secretAnswer) {
      toast.error('يرجى إدخال السؤال والإجابة');
      return;
    }

    setSavingSecret(true);
    try {
      const response = await fetch(`${API_URL}/api/teacher/secret-question`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          secret_question: secretQuestion,
          secret_answer: secretAnswer,
        }),
      });

      if (response.ok) {
        toast.success('تم حفظ السؤال السري بنجاح');
        setHasSecretQuestion(true);
        setSecretQuestionDialogOpen(false);
        setSecretAnswer('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'فشل حفظ السؤال السري');
      }
    } catch (error) {
      toast.error('حدث خطأ أثناء الحفظ');
    }
    setSavingSecret(false);
  };

  const fetchExams = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exams`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setExams(data);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchViolations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/violations/filtered`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setViolations(data.violations || []);
        setFilteredViolations(data.violations || []);
        setViolationTypes(data.violation_types || []);
        setViolationSummary(data.student_summary || []);
      }
    } catch (error) {
      console.error('Error fetching violations:', error);
    }
  };

  const fetchFilteredViolations = async () => {
    try {
      let url = `${API_URL}/api/teacher/violations/filtered?`;
      if (violationFilters.examId !== 'all') url += `exam_id=${violationFilters.examId}&`;
      if (violationFilters.studentId !== 'all') url += `student_id=${violationFilters.studentId}&`;
      if (violationFilters.violationType !== 'all') url += `violation_type=${violationFilters.violationType}&`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setFilteredViolations(data.violations || []);
        setViolationSummary(data.student_summary || []);
      }
    } catch (error) {
      console.error('Error fetching filtered violations:', error);
    }
  };

  // Refetch violations when filters change
  useEffect(() => {
    if (token && can('exams')) {
      fetchFilteredViolations();
    }
  }, [violationFilters]);

  const fetchPendingStudents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/students/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPendingStudents(data);
      }
    } catch (error) {
      console.error('Error fetching pending students:', error);
    }
  };

  const fetchAllStudents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/students/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAllStudents(data);
      }
    } catch (error) {
      console.error('Error fetching all students:', error);
    }
  };

  const fetchPasswordResets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/password-resets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPasswordResets(data);
      }
    } catch (error) {
      console.error('Error fetching password resets:', error);
    }
  };

  const generateAccessCode = async (examId) => {
    setGeneratingCode(examId);
    try {
      const response = await fetch(`${API_URL}/api/teacher/exam-access-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exam_id: examId,
          valid_minutes: codeValidityMinutes,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setExamAccessCodes(prev => ({
          ...prev,
          [examId]: [...(prev[examId] || []), data]
        }));
        toast.success(`تم إنشاء رمز الدخول: ${data.code}`);
        return data;
      }
    } catch (error) {
      console.error('Error generating access code:', error);
      toast.error('فشل إنشاء رمز الدخول');
    }
    setGeneratingCode(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('تم النسخ للحافظة');
  };

  const copyRegistrationLink = () => {
    const link = `${FRONTEND_URL}/student/register?teacher=${teacherCode}`;
    navigator.clipboard.writeText(link);
    toast.success('تم نسخ رابط التسجيل');
  };

  const getViolationTypeLabel = (type) => {
    const labels = {
      'tab_switch': 'تبديل النافذة',
      'focus_loss': 'فقدان التركيز',
      'fullscreen_exit': 'الخروج من ملء الشاشة',
      'screenshot_attempt': 'محاولة لقطة شاشة',
      'screen_record_attempt': 'محاولة تسجيل الشاشة',
      'copy_paste': 'نسخ/لصق',
      'right_click': 'النقر الأيمن'
    };
    return labels[type] || type;
  };

  const exportStudents = async () => {
    try {
      const params = new URLSearchParams();
      if (gradeFilter !== 'all') params.append('grade', gradeFilter);
      if (sectionFilter !== 'all') params.append('section', sectionFilter);
      
      const response = await fetch(`${API_URL}/api/teacher/export/students?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Convert to CSV
        const headers = ['الاسم', 'اسم المستخدم', 'الصف', 'الشعبة', 'المدرسة', 'الهاتف', 'الحالة'];
        const rows = data.students.map(s => [
          s.full_name || s.name || '',
          s.username,
          s.grade,
          s.section,
          s.school || '',
          s.phone_number || '',
          s.status === 'approved' ? 'معتمد' : s.status === 'pending' ? 'قيد الانتظار' : 'مرفوض'
        ]);
        
        // Use new Excel export instead
        exportStudentsToExcel();
      }
    } catch (error) {
      console.error('Error exporting students:', error);
      toast.error('فشل تصدير القائمة');
    }
  };

  const exportExamResults = async (examId) => {
    // Find exam title
    const exam = exams.find(e => e.id === examId);
    const examTitle = exam?.title || 'اختبار';
    
    // Use new Excel export
    await exportExamResultsToExcel(examId, examTitle);
  };

  const deleteStudent = async () => {
    if (!studentToDelete) return;
    
    setDeletingStudent(true);
    try {
      const response = await fetch(`${API_URL}/api/teacher/students/${studentToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        toast.success(`تم حذف الطالب ${studentToDelete.full_name || studentToDelete.username}`);
        // Refresh student lists
        setAllStudents(prev => prev.filter(s => s.id !== studentToDelete.id));
        setPendingStudents(prev => prev.filter(s => s.id !== studentToDelete.id));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'فشل حذف الطالب');
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('حدث خطأ أثناء حذف الطالب');
    }
    
    setDeletingStudent(false);
    setDeleteStudentDialogOpen(false);
    setStudentToDelete(null);
  };

  // Filter students
  const normalizedStudentSearch = studentSearch.trim().toLowerCase();
  const studentMatchesSearch = (s) => {
    if (!normalizedStudentSearch) return true;
    const haystack = [
      s.full_name, s.name,
      s.first_name, s.second_name, s.third_name, s.family_name,
      s.username, s.phone_number, s.school,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(normalizedStudentSearch);
  };

  const filteredPendingStudents = pendingStudents.filter(student => {
    if (gradeFilter !== 'all' && student.grade !== gradeFilter) return false;
    if (sectionFilter !== 'all' && student.section !== sectionFilter) return false;
    if (!studentMatchesSearch(student)) return false;
    return true;
  });

  const filteredAllStudents = allStudents.filter(student => {
    if (gradeFilter !== 'all' && student.grade !== gradeFilter) return false;
    if (sectionFilter !== 'all' && student.section !== sectionFilter) return false;
    if (statusFilter === 'online') {
      if (!onlineStudentIds.has(student.id)) return false;
    } else if (statusFilter !== 'all' && student.status !== statusFilter) return false;
    if (!studentMatchesSearch(student)) return false;
    return true;
  });

  // Students currently connected (online now) — "الطلاب النشطون" = المتصلون الآن
  const onlineCount = onlineStudentIds.size;

  const handleDeleteExam = async () => {
    if (!examToDelete) return;

    try {
      const response = await fetch(`${API_URL}/api/exams/${examToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('تم حذف الاختبار بنجاح');
        fetchExams();
      } else {
        toast.error('فشل حذف الاختبار');
      }
    } catch (error) {
      toast.error('حدث خطأ أثناء الحذف');
    }
    setDeleteDialogOpen(false);
    setExamToDelete(null);
  };

  const handleReleaseResults = async (examId) => {
    setReleasingResults(examId);
    try {
      const response = await fetch(`${API_URL}/api/teacher/exams/${examId}/release-results`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('تم إصدار النتائج بنجاح - يمكن للطلاب الآن رؤية نتائجهم');
        fetchExams(); // Refresh exams to update results_released status
      } else {
        const error = await response.json();
        toast.error(error.detail || 'فشل إصدار النتائج');
      }
    } catch (error) {
      console.error('Error releasing results:', error);
      toast.error('حدث خطأ أثناء إصدار النتائج');
    }
    setReleasingResults(null);
  };

  const handleApproveStudent = async () => {
    if (!selectedStudent) return;
    
    setProcessingStudent(selectedStudent.id);
    try {
      const response = await fetch(`${API_URL}/api/teacher/students/${selectedStudent.id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: '' }),
      });

      if (response.ok) {
        toast.success(`تم تفعيل حساب الطالب ${selectedStudent.name}`);
        fetchPendingStudents();
        fetchAllStudents();
      } else {
        toast.error('فشل تفعيل الحساب');
      }
    } catch (error) {
      toast.error('حدث خطأ أثناء التفعيل');
    }
    setProcessingStudent(null);
    setApprovalDialogOpen(false);
    setSelectedStudent(null);
  };

  const handleRejectStudent = async () => {
    if (!selectedStudent) return;
    
    setProcessingStudent(selectedStudent.id);
    try {
      const response = await fetch(`${API_URL}/api/teacher/students/${selectedStudent.id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: rejectionNote }),
      });

      if (response.ok) {
        toast.success(`تم رفض طلب الطالب ${selectedStudent.name}`);
        fetchPendingStudents();
        fetchAllStudents();
      } else {
        toast.error('فشل رفض الطلب');
      }
    } catch (error) {
      toast.error('حدث خطأ أثناء الرفض');
    }
    setProcessingStudent(null);
    setRejectionDialogOpen(false);
    setSelectedStudent(null);
    setRejectionNote('');
  };

  const handleDeactivateStudent = async (student) => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/students/${student.id}/deactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: 'إلغاء تفعيل من لوحة التحكم' }),
      });

      if (response.ok) {
        toast.success(`تم إلغاء تفعيل الطالب ${student.full_name || student.name}`);
        fetchAllStudents();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'فشل إلغاء التفعيل');
      }
    } catch (error) {
      toast.error('حدث خطأ أثناء إلغاء التفعيل');
    }
  };

  const handleReactivateStudent = async (student) => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/students/${student.id}/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: 'إعادة تفعيل من لوحة التحكم' }),
      });

      if (response.ok) {
        toast.success(`تم إعادة تفعيل الطالب ${student.full_name || student.name}`);
        fetchAllStudents();
        fetchPendingStudents();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'فشل إعادة التفعيل');
      }
    } catch (error) {
      toast.error('حدث خطأ أثناء إعادة التفعيل');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Open visibility settings dialog for an exam
  const openVisibilityDialog = (exam) => {
    setSelectedExamForVisibility(exam);
    setVisibilitySettings({
      show_score_to_student: exam.show_score_to_student ?? true,
      show_correct_answers: exam.show_correct_answers ?? false,
      show_wrong_answers: exam.show_wrong_answers ?? false,
      show_answer_review: exam.show_answer_review ?? false,
      show_percentage: exam.show_percentage ?? true,
      show_pass_fail_only: exam.show_pass_fail_only ?? false,
    });
    setVisibilityDialogOpen(true);
  };

  const handleSaveVisibility = async () => {
    if (!selectedExamForVisibility) return;
    setSavingVisibility(true);
    try {
      const response = await fetch(`${API_URL}/api/exams/${selectedExamForVisibility.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(visibilitySettings),
      });
      if (response.ok) {
        toast.success('تم حفظ إعدادات الظهور');
        fetchExams();
        setVisibilityDialogOpen(false);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'فشل الحفظ');
      }
    } catch {
      toast.error('حدث خطأ أثناء الحفظ');
    }
    setSavingVisibility(false);
  };

  // Open edit student dialog
  const openEditStudentDialog = (student) => {
    setStudentToEdit(student);
    setEditStudentForm({
      first_name: student.first_name || '',
      second_name: student.second_name || '',
      third_name: student.third_name || '',
      family_name: student.family_name || '',
      username: student.username || '',
      date_of_birth: student.date_of_birth || '',
      phone_number: student.phone_number || '',
      school: student.school || '',
      grade: student.grade || '',
      section: student.section || '',
    });
    setEditStudentDialogOpen(true);
  };

  const handleSaveEditStudent = async () => {
    if (!studentToEdit) return;
    setSavingStudent(true);
    try {
      const response = await fetch(`${API_URL}/api/teacher/students/${studentToEdit.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editStudentForm),
      });
      if (response.ok) {
        toast.success('تم تحديث بيانات الطالب');
        fetchAllStudents();
        setEditStudentDialogOpen(false);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'فشل تحديث البيانات');
      }
    } catch {
      toast.error('حدث خطأ أثناء الحفظ');
    }
    setSavingStudent(false);
  };

  // Export exam results to Excel
  const exportExamResultsToExcel = async (examId, examTitle) => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/exams/${examId}/results`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        toast.error('فشل تحميل النتائج');
        return;
      }
      
      const results = await response.json();
      
      // Sheet 1: Results
      const resultsData = results.map((r, idx) => ({
        '#': idx + 1,
        'اسم الطالب': r.student_name,
        'الصف': r.student_grade,
        'الشعبة': r.student_section,
        'الدرجة': r.score || 0,
        'الدرجة الكلية': r.total_points || 0,
        'النسبة المئوية': r.total_points ? Math.round((r.score / r.total_points) * 100) + '%' : '0%',
        'الحالة': r.status === 'completed' ? 'مكتمل' : r.status === 'terminated' ? 'منهي (مخالفات)' : r.status,
        'عدد المخالفات': r.violation_count || 0,
        'وقت البدء': r.started_at ? new Date(r.started_at).toLocaleString('ar-SA') : '-',
        'وقت الانتهاء': r.ended_at ? new Date(r.ended_at).toLocaleString('ar-SA') : '-'
      }));
      
      // Sheet 2: Statistics
      const scores = results.filter(r => r.score !== undefined).map(r => r.score);
      const avgScore = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : 0;
      const passCount = results.filter(r => r.total_points && (r.score / r.total_points) >= 0.5).length;
      
      const statsData = [{
        'عنوان الاختبار': examTitle,
        'تاريخ التصدير': new Date().toLocaleString('ar-SA'),
        'عدد الطلاب': results.length,
        'متوسط الدرجات': avgScore,
        'نسبة النجاح': results.length ? Math.round((passCount / results.length) * 100) + '%' : '0%',
        'أعلى درجة': scores.length ? Math.max(...scores) : 0,
        'أدنى درجة': scores.length ? Math.min(...scores) : 0
      }];
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      const ws1 = XLSX.utils.json_to_sheet(resultsData);
      XLSX.utils.book_append_sheet(wb, ws1, 'النتائج');
      
      const ws2 = XLSX.utils.json_to_sheet(statsData);
      XLSX.utils.book_append_sheet(wb, ws2, 'إحصائيات');
      
      // Download
      const fileName = `نتائج_${examTitle}_${new Date().toLocaleDateString('ar-SA')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success('تم تصدير النتائج بنجاح');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('حدث خطأ أثناء التصدير');
    }
  };

  // Export all exams' results (for all students, all exams owned by teacher)
  const exportAllExamResultsToExcel = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/export/all-exam-results`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        toast.error('فشل تحميل النتائج');
        return;
      }
      const data = await response.json();
      const rows = (data.results || []).map((r, idx) => ({
        '#': idx + 1,
        'الاختبار': r.exam_title || '-',
        'اسم الطالب': r.student_name || '-',
        'الصف': r.student_grade || '-',
        'الشعبة': r.student_section || '-',
        'الدرجة': r.score ?? 0,
        'الدرجة الكلية': r.total_points ?? 0,
        'النسبة المئوية': r.total_points ? Math.round((r.score / r.total_points) * 100) + '%' : '0%',
        'الحالة': r.status === 'completed' ? 'مكتمل' : r.status === 'terminated' ? 'منهي (مخالفات)' : (r.status || '-'),
        'عدد المخالفات': r.violation_count || 0,
        'وقت البدء': r.started_at ? new Date(r.started_at).toLocaleString('ar-SA') : '-',
        'وقت الانتهاء': r.ended_at ? new Date(r.ended_at).toLocaleString('ar-SA') : '-',
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, 'كل النتائج');
      const fileName = `كل_نتائج_الاختبارات_${new Date().toLocaleDateString('ar-SA')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success('تم تصدير كل النتائج');
    } catch (e) {
      console.error('Export all exam results error:', e);
      toast.error('حدث خطأ أثناء التصدير');
    }
  };

  // Export all topic (question-bank) results
  const exportAllTopicResultsToExcel = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teacher/export/all-topic-results`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        toast.error('فشل تحميل النتائج');
        return;
      }
      const data = await response.json();
      const rows = (data.results || []).map((r, idx) => ({
        '#': idx + 1,
        'الموضوع': r.topic_title || '-',
        'اسم الطالب': r.student_name || '-',
        'الصف': r.student_grade || '-',
        'الشعبة': r.student_section || '-',
        'رقم المحاولة': r.attempt_number || 1,
        'الدرجة': r.score ?? 0,
        'الدرجة الكلية': r.total_points ?? 0,
        'النسبة المئوية': r.total_points ? Math.round((r.score / r.total_points) * 100) + '%' : '0%',
        'الحالة': r.status === 'completed' ? 'مكتمل' : r.status === 'terminated' ? 'منهي (مخالفات)' : r.status === 'in_progress' ? 'قيد التنفيذ' : (r.status || '-'),
        'عدد المخالفات': r.violation_count || 0,
        'وقت البدء': r.started_at ? new Date(r.started_at).toLocaleString('ar-SA') : '-',
        'وقت الانتهاء': r.ended_at ? new Date(r.ended_at).toLocaleString('ar-SA') : '-',
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, 'نتائج بنك الأسئلة');
      const fileName = `كل_نتائج_بنك_الأسئلة_${new Date().toLocaleDateString('ar-SA')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success('تم تصدير كل النتائج');
    } catch (e) {
      console.error('Export all topic results error:', e);
      toast.error('حدث خطأ أثناء التصدير');
    }
  };

  // Open export-to-bank dialog
  const openExportToBank = (exam) => {
    setExportBankExam(exam);
    const map = {};
    (exam.questions || []).forEach(q => { map[q.id] = true; });
    setExportBankSelectedIds(map);
    setExportBankTopicId('');
    setExportBankOpen(true);
  };

  const submitExportToBank = async () => {
    if (!exportBankExam || !exportBankTopicId) {
      toast.error('اختر موضوعاً في بنك الأسئلة');
      return;
    }
    const chosen = (exportBankExam.questions || []).filter(q => exportBankSelectedIds[q.id]);
    if (chosen.length === 0) {
      toast.error('اختر سؤالاً واحداً على الأقل');
      return;
    }
    setExportingToBank(true);
    try {
      const payload = {
        questions: chosen.map(q => {
          const opts = (q.options || []).map(o => ({ id: o.id, text: o.text }));
          const correctOpt = opts.find(o => o.id === q.correct_answer);
          return {
            question_type: q.question_type,
            text: q.text,
            options: opts,
            correct_answer: correctOpt ? correctOpt.id : (q.correct_answer || ''),
            points: q.points ?? 1,
            difficulty: q.difficulty || 'medium',
          };
        }),
      };
      const r = await fetch(`${API_URL}/api/topics/${exportBankTopicId}/questions/bulk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success(`تم تصدير ${chosen.length} سؤال إلى بنك الأسئلة`);
        setExportBankOpen(false);
        await fetchTopics();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'فشل التصدير');
      }
    } catch {
      toast.error('حدث خطأ أثناء التصدير');
    }
    setExportingToBank(false);
  };

  // Export students list to Excel
  const exportStudentsToExcel = () => {
    const studentsData = allStudents.map((s, idx) => ({
      '#': idx + 1,
      'الاسم الكامل': s.full_name || s.name || `${s.first_name} ${s.second_name} ${s.third_name} ${s.family_name}`,
      'اسم المستخدم': s.username,
      'الصف': s.grade,
      'الشعبة': s.section,
      'المدرسة': s.school || '-',
      'الحالة': s.status === 'approved' ? 'مفعّل' : s.status === 'pending' ? 'قيد المراجعة' : s.status === 'deactivated' ? 'معطّل' : s.status,
      'تاريخ التسجيل': s.created_at ? new Date(s.created_at).toLocaleDateString('ar-SA') : '-'
    }));
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(studentsData);
    XLSX.utils.book_append_sheet(wb, ws, 'الطلاب');
    
    const fileName = `قائمة_الطلاب_${new Date().toLocaleDateString('ar-SA')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('تم تصدير قائمة الطلاب');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">مفعّل</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800">قيد المراجعة</Badge>;
      case 'rejected':
        return <Badge variant="destructive">مرفوض</Badge>;
      case 'deactivated':
        return <Badge className="bg-[#EF9F27]/10 text-[#412402]">معطّل</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const stats = [
    {
      title: 'المتصلون الآن',
      value: onlineCount,
      icon: <Users className="w-6 h-6" />,
      color: 'bg-green-100 text-green-700',
      tab: 'students',
    },
    {
      title: 'إجمالي الاختبارات',
      value: exams.length,
      icon: <FileText className="w-6 h-6" />,
      color: 'bg-blue-100 text-blue-700',
      tab: 'exams',
    },
    {
      title: 'طلبات التسجيل',
      value: pendingStudents.length,
      icon: <UserCheck className="w-6 h-6" />,
      color: 'bg-amber-100 text-amber-700',
      tab: 'students',
    },
    {
      title: 'الطلاب المفعّلين',
      value: allStudents.filter(s => s.status === 'approved').length,
      icon: <Users className="w-6 h-6" />,
      color: 'bg-green-100 text-green-700',
      tab: 'students',
    },
    {
      title: 'المخالفات المسجلة',
      value: violations.length,
      icon: <AlertTriangle className="w-6 h-6" />,
      color: 'bg-red-100 text-red-700',
      tab: 'violations',
    },
  ].filter((s) => can(TAB_PERMS[s.tab]));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#EF9F27] mx-auto mb-4" />
          <p className="text-[#412402]/60">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="bg-[#FFFFFF] border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
              <span className="text-xl font-bold text-[#EF9F27]">المعتمد في اللغة العربية</span>
            </Link>
            <div className="flex items-center gap-4">
              <NotificationCenter />
              <span className="text-[#412402]/60 hidden sm:block">مرحباً، {teacher?.name}{isHelper ? ' (معلم مساعد)' : ''}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-[#E8DFCF] text-[#EF9F27] hover:bg-[#EF9F27]/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 ml-2" />
                خروج
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#EF9F27]">لوحة التحكم</h1>
            <p className="text-[#412402]/50 mt-1">إدارة الاختبارات والطلاب</p>
          </div>
          {can('exams') && <Button 
            onClick={() => navigate('/teacher/exams/create')}
            className="bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] font-bold shadow-[0_4px_20px_rgba(212,168,83,0.3)]"
            data-testid="create-exam-btn"
          >
            <Plus className="w-5 h-5 ml-2" />
            إنشاء اختبار جديد
          </Button>}
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <Card
              key={index}
              className="bg-[#FFFFFF]/80 border border-[#E8DFCF] hover:border-[#d4a853]/50 transition-all shadow-[0_0_20px_rgba(212,168,83,0.05)] hover:shadow-lg cursor-pointer"
              onClick={() => { setActiveTab(stat.tab); document.getElementById('dashboard-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#412402]/50 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-[#412402]">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-[#EF9F27]/10 text-[#EF9F27]`}>
                    {stat.icon}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Teacher Registration Link */}
        {teacherCode && (
          <Card className="bg-[#FFFFFF]/80 border border-[#E8DFCF] mb-8 shadow-[0_0_20px_rgba(212,168,83,0.05)]">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#EF9F27]/10 border border-[#E8DFCF] rounded-lg flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-[#EF9F27]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#EF9F27]">رابط تسجيل الطلاب</h3>
                    <p className="text-sm text-[#412402]/50">شارك هذا الرابط مع طلابك للتسجيل تلقائياً تحت إشرافك</p>
                  </div>
                </div>
                <div className="flex-1 flex items-center gap-2 bg-[#FFFFFF] rounded-lg border border-[#E8DFCF] p-2">
                  <div className="flex-1 flex items-center gap-2">
                    <Badge className="bg-[#EF9F27]/10 text-[#EF9F27] border border-[#E8DFCF] font-mono">
                      {teacherCode}
                    </Badge>
                    <span className="text-sm text-[#412402]/40 truncate hidden sm:inline" dir="ltr">
                      {FRONTEND_URL}/student/register?teacher={teacherCode}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(teacherCode)}
                    className="shrink-0 border-[#E8DFCF] text-[#EF9F27] hover:bg-[#EF9F27]/10"
                    data-testid="copy-code-btn"
                  >
                    <Copy className="w-4 h-4 ml-1" />
                    نسخ الرمز
                  </Button>
                  <Button
                    size="sm"
                    onClick={copyRegistrationLink}
                    className="bg-amber-600 hover:bg-amber-700 shrink-0"
                    data-testid="copy-link-btn"
                  >
                    <LinkIcon className="w-4 h-4 ml-1" />
                    نسخ الرابط
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account security + helper accounts are for the owner teacher only */}
        {!isHelper && (<>
        {/* Secret Question Card */}
        <Card className={`border-amber-200 ${hasSecretQuestion ? 'bg-amber-50/30' : 'bg-amber-50/50'} mb-8`}>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hasSecretQuestion ? 'bg-green-100' : 'bg-amber-100'}`}>
                  <KeyRound className={`w-5 h-5 ${hasSecretQuestion ? 'text-green-700' : 'text-amber-700'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#412402]">السؤال السري لاستعادة كلمة المرور</h3>
                  <p className="text-sm text-[#412402]/60">
                    {hasSecretQuestion 
                      ? 'تم تعيين السؤال السري - يمكن للطلاب استعادة كلمة مرورهم باستخدام إجابتك'
                      : 'قم بتعيين سؤال سري ليتمكن الطلاب من استعادة كلمة مرورهم'
                    }
                  </p>
                </div>
              </div>
              <Button
                variant={hasSecretQuestion ? "outline" : "default"}
                onClick={() => setSecretQuestionDialogOpen(true)}
                className={hasSecretQuestion ? "" : "bg-amber-600 hover:bg-amber-700"}
                data-testid="secret-question-btn"
              >
                <KeyRound className="w-4 h-4 ml-1" />
                {hasSecretQuestion ? 'تعديل السؤال السري' : 'تعيين السؤال السري'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recovery Codes Card */}
        <Card className={`border-blue-200 ${recoveryCodesRemaining > 0 ? 'bg-blue-50/30' : 'bg-red-50/30'} mb-8`}>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${recoveryCodesRemaining > 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
                  <Key className={`w-5 h-5 ${recoveryCodesRemaining > 0 ? 'text-blue-700' : 'text-red-700'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#412402]">رموز الاستعادة الاحتياطية</h3>
                  <p className="text-sm text-[#412402]/60">
                    {recoveryCodesRemaining > 0
                      ? `متبقٍ ${recoveryCodesRemaining} رمز — استخدمها عند نسيان كلمة المرور والسؤال السري`
                      : 'لا توجد رموز — يُنصح بتوليد رموز احتياطية الآن'}
                  </p>
                </div>
              </div>
              <Button
                variant={recoveryCodesRemaining > 0 ? 'outline' : 'default'}
                onClick={() => { setGeneratedCodes([]); setRecoveryCodesDialogOpen(true); }}
                className={recoveryCodesRemaining === 0 ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
              >
                <RefreshCw className="w-4 h-4 ml-1" />
                {recoveryCodesRemaining > 0 ? 'توليد رموز جديدة' : 'توليد رموز الاستعادة'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <TeacherHelpers apiUrl={API_URL} token={token} />
        </>)}

        {/* Tabs */}
        <Tabs id="dashboard-tabs" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap w-full gap-1">
            {can('exams') && <TabsTrigger value="exams">الاختبارات</TabsTrigger>}
            {can('question_bank') && <TabsTrigger value="question-bank">بنك الأسئلة</TabsTrigger>}
            {can('videos') && <TabsTrigger value="videos" className="flex items-center gap-1">
              <Video className="w-4 h-4" />
              الفيديوهات
            </TabsTrigger>}
            {can('wallet') && <TabsTrigger value="wallet" className="flex items-center gap-1">
              <Wallet className="w-4 h-4" />
              الأرصدة
            </TabsTrigger>}
            {can('students') && <TabsTrigger value="students" className="relative">
              الطلاب
              {pendingStudents.length > 0 && (
                <span className="absolute -top-1 -left-1 w-5 h-5 bg-red-500 text-[#412402] text-xs rounded-full flex items-center justify-center">
                  {pendingStudents.length}
                </span>
              )}
            </TabsTrigger>}
            {can('exams') && <TabsTrigger value="violations">المخالفات</TabsTrigger>}
            {can('news') && <TabsTrigger value="news" className="flex items-center gap-1">
              <Newspaper className="w-4 h-4" />
              شريط الأخبار
            </TabsTrigger>}
          </TabsList>

          {/* Exams Tab */}
          <TabsContent value="exams">
            <Card className="border-[#E8DFCF]">
              <CardHeader className="border-b border-[#E8DFCF]">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="text-lg font-semibold text-[#412402]">الاختبارات</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportAllExamResultsToExcel}
                    className="text-green-700 hover:text-green-800 hover:bg-green-50 border-green-200"
                    data-testid="export-all-exam-results-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 ml-1" />
                    تصدير علامات كل الاختبارات
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {exams.length === 0 ? (
                  <div className="py-12 text-center">
                    <FileText className="w-12 h-12 text-[#412402]/30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-[#412402]/80 mb-2">لا توجد اختبارات</h3>
                    <p className="text-[#412402]/50 mb-4">ابدأ بإنشاء اختبارك الأول</p>
                    <Button 
                      onClick={() => navigate('/teacher/exams/create')}
                      className="bg-amber-700 hover:bg-amber-800"
                    >
                      <Plus className="w-4 h-4 ml-2" />
                      إنشاء اختبار
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {exams.map((exam) => (
                      <div 
                        key={exam.id} 
                        className="p-4 hover:bg-[#FFFFFF] transition-colors"
                        data-testid={`exam-item-${exam.id}`}
                      >
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h3 className="font-semibold text-[#412402]">{exam.title}</h3>
                                <Badge variant={exam.is_active ? "default" : "secondary"}>
                                  {exam.is_active ? 'نشط' : 'غير نشط'}
                                </Badge>
                                {exam.require_access_code && (
                                  <Badge variant="outline" className="gap-1">
                                    <Key className="w-3 h-3" />
                                    رمز دخول
                                  </Badge>
                                )}
                                {/* Result Release Status Badge */}
                                {exam.result_release_mode === 'manual' && (
                                  <Badge 
                                    variant={exam.results_released ? "default" : "secondary"}
                                    className={`gap-1 ${exam.results_released ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}
                                  >
                                    {exam.results_released ? (
                                      <>
                                        <Unlock className="w-3 h-3" />
                                        النتائج متاحة
                                      </>
                                    ) : (
                                      <>
                                        <Lock className="w-3 h-3" />
                                        النتائج محجوبة
                                      </>
                                    )}
                                  </Badge>
                                )}
                                {exam.result_release_mode === 'scheduled' && (
                                  <Badge variant="outline" className="gap-1 bg-blue-50 text-blue-700">
                                    <Clock className="w-3 h-3" />
                                    إصدار مجدول
                                  </Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-4 text-sm text-[#412402]/60">
                                <span className="flex items-center gap-1">
                                  <BookOpen className="w-4 h-4" />
                                  {exam.grade}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {exam.duration_minutes} دقيقة
                                </span>
                                <span className="flex items-center gap-1">
                                  <FileText className="w-4 h-4" />
                                  {exam.questions?.length || 0} سؤال
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Release Results Button - Only for manual release exams that haven't been released */}
                              {exam.result_release_mode === 'manual' && !exam.results_released && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleReleaseResults(exam.id)}
                                  disabled={releasingResults === exam.id}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                                  data-testid={`release-results-btn-${exam.id}`}
                                >
                                  {releasingResults === exam.id ? (
                                    <Loader2 className="w-4 h-4 ml-1 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4 ml-1" />
                                  )}
                                  إصدار النتائج
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openVisibilityDialog(exam)}
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
                                data-testid={`visibility-btn-${exam.id}`}
                                title="إعدادات ظهور النتائج"
                              >
                                <Eye className="w-4 h-4 ml-1" />
                                الظهور
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/teacher/exams/${exam.id}/edit`)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`edit-exam-btn-${exam.id}`}
                              >
                                <Edit className="w-4 h-4 ml-1" />
                                تعديل
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => navigate(`/teacher/exams/${exam.id}/monitor`)}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                data-testid={`monitor-btn-${exam.id}`}
                              >
                                <Eye className="w-4 h-4 ml-1" />
                                مراقبة مباشرة
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => navigate(`/teacher/exams/${exam.id}/results`)}
                                data-testid={`view-results-btn-${exam.id}`}
                              >
                                <FileText className="w-4 h-4 ml-1" />
                                النتائج
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => exportExamResults(exam.id)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`export-results-btn-${exam.id}`}
                              >
                                <FileSpreadsheet className="w-4 h-4 ml-1" />
                                Excel
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => navigate(`/teacher/exams/${exam.id}/analytics`)}
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                data-testid={`analytics-btn-${exam.id}`}
                              >
                                <BarChart3 className="w-4 h-4 ml-1" />
                                تحليلات
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/teacher/exams/${exam.id}/essay-grading`)}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                data-testid={`essay-grading-btn-${exam.id}`}
                              >
                                <Edit className="w-4 h-4 ml-1" />
                                تصحيح المقالي
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openExportToBank(exam)}
                                className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 border-amber-200"
                                data-testid={`export-to-bank-btn-${exam.id}`}
                                title="تصدير الأسئلة إلى بنك الأسئلة"
                              >
                                <BookOpen className="w-4 h-4 ml-1" />
                                إلى بنك الأسئلة
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  setExamToDelete(exam.id);
                                  setDeleteDialogOpen(true);
                                }}
                                data-testid={`delete-exam-btn-${exam.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Access Code Section */}
                          {exam.require_access_code && (
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                              <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <KeyRound className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm text-blue-800 font-medium">رموز الدخول</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {examAccessCodes[exam.id]?.filter(c => c.is_active && new Date(c.expires_at) > new Date()).slice(-1).map(code => (
                                    <div key={code.id} className="flex items-center gap-2 bg-[#FFFFFF]/80 px-3 py-1 rounded-md border border-blue-200">
                                      <span className="font-mono font-bold text-blue-700">{code.code}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => copyToClipboard(code.code)}
                                      >
                                        <Copy className="w-3 h-3" />
                                      </Button>
                                      <span className="text-xs text-[#412402]/50">
                                        صالح حتى {new Date(code.expires_at).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                  ))}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => generateAccessCode(exam.id)}
                                    disabled={generatingCode === exam.id}
                                    className="text-blue-600 border-blue-200 hover:bg-blue-100"
                                  >
                                    {generatingCode === exam.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <Plus className="w-4 h-4 ml-1" />
                                        رمز جديد
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Export Button */}
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => exportExamResults(exam.id)}
                              className="text-[#412402]/60"
                            >
                              <Download className="w-4 h-4 ml-1" />
                              تصدير النتائج
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Question Bank Tab */}
          <TabsContent value="question-bank" className="space-y-4">
            <div className="flex justify-end gap-2">
              <Button
                variant={showBankAnalytics ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowBankAnalytics(v => !v)}
                className={showBankAnalytics ? 'bg-amber-700 hover:bg-amber-800' : 'text-amber-700 border-amber-200 hover:bg-amber-50'}
                data-testid="bank-analytics-btn"
              >
                <BarChart3 className="w-4 h-4 ml-1" />
                تحليلات بنك الأسئلة
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportAllTopicResultsToExcel}
                className="text-green-700 hover:text-green-800 hover:bg-green-50 border-green-200"
                data-testid="export-all-topic-results-btn"
              >
                <FileSpreadsheet className="w-4 h-4 ml-1" />
                تصدير علامات بنك الأسئلة
              </Button>
            </div>

            {showBankAnalytics && (
              <div className="p-3 bg-gradient-to-br from-[#FAF6EC] to-[#F1E8D2] rounded-2xl border border-[#E8DFCF]">
                <TopicBankAnalytics topics={topics} attempts={allTopicAttempts} />
              </div>
            )}
            <TeacherQuestionBank
              topics={topics}
              attempts={allTopicAttempts}
              apiUrl={API_URL}
              token={token}
              onCreateClick={() => { resetTopicForm(); setTopicDialogOpen(true); }}
              onCreateInFolder={(folderPath) => {
                resetTopicForm();
                setTopicForm(prev => ({ ...prev, category: folderPath }));
                setTopicDialogOpen(true);
              }}
              onManage={(topic) => navigate(`/teacher/topics/${topic.id}`)}
              onDelete={(topic) => { setTopicToDelete(topic); setDeleteTopicDialogOpen(true); }}
              onRefresh={fetchTopics}
            />

            {/* Mock paid-access control (front-end only, no real payment) */}
            <div className="pt-4 mt-2 border-t border-[#E8DFCF]">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-bold text-[#412402]">الوصول المدفوع للمجلدات</h3>
                {/* <Badge className="bg-amber-100 text-amber-800 text-[10px]">تجريبي</Badge> */}
              </div>
              <BankAccessManager topics={topics} students={allStudents} videos={bankVideos} apiUrl={API_URL} token={token} />
            </div>
          </TabsContent>

          {/* Recorded Videos Tab */}
          <TabsContent value="videos" className="space-y-4">
            <TeacherVideoManager apiUrl={API_URL} token={token} />
          </TabsContent>

          {/* Wallet / Balances Tab */}
          <TabsContent value="wallet" className="space-y-4">
            <TeacherWallet apiUrl={API_URL} token={token} />
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students" className="space-y-6">
            {/* Filters */}
            <Card className="border-[#E8DFCF]">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-[#412402]/50" />
                    <span className="text-sm font-medium text-[#412402]/80">فلترة:</span>
                  </div>
                  <Select value={gradeFilter} onValueChange={setGradeFilter}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="الصف" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">جميع الصفوف</SelectItem>
                      {grades.map(g => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={sectionFilter} onValueChange={setSectionFilter}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="الشعبة" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">جميع الشعب</SelectItem>
                      {sections.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px]" data-testid="student-status-filter">
                      <SelectValue placeholder="الحالة" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">كل الحالات</SelectItem>
                      <SelectItem value="online">المتصلون الآن</SelectItem>
                      <SelectItem value="approved">المعتمدون</SelectItem>
                      <SelectItem value="pending">قيد المراجعة</SelectItem>
                      <SelectItem value="deactivated">معطّل</SelectItem>
                      <SelectItem value="rejected">مرفوض</SelectItem>
                    </SelectContent>
                  </Select>
                  <Badge className="bg-green-100 text-green-700 border border-green-200 flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      {onlineCount > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>}
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${onlineCount > 0 ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                    </span>
                    المتصلون الآن: {onlineCount}
                  </Badge>
                  <div className="relative">
                    <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#412402]/40 pointer-events-none" />
                    <Input
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      placeholder="ابحث باسم الطالب…"
                      className="pr-8 w-56"
                      data-testid="student-search-input"
                      dir="auto"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setGradeFilter('all'); setSectionFilter('all'); setStatusFilter('all'); setStudentSearch(''); }}
                  >
                    <RefreshCw className="w-4 h-4 ml-1" />
                    إعادة تعيين
                  </Button>
                  <div className="mr-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportStudents}
                      className="border-green-200 text-green-700 hover:bg-green-50"
                      data-testid="export-students-btn"
                    >
                      <FileSpreadsheet className="w-4 h-4 ml-1" />
                      تصدير Excel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Students */}
            {filteredPendingStudents.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader className="border-b border-amber-200">
                  <CardTitle className="text-lg font-semibold text-[#412402] flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-600" />
                    طلبات التسجيل المعلقة ({filteredPendingStudents.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-amber-100">
                    {filteredPendingStudents.map((student) => (
                      <div 
                        key={student.id} 
                        className="p-4 hover:bg-amber-100/50 transition-colors"
                        data-testid={`pending-student-${student.id}`}
                      >
                        <div className="flex flex-col gap-4">
                          {/* Student Info Grid */}
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <h3 className="font-bold text-lg text-[#412402]">
                                {student.full_name || student.name}
                              </h3>
                              <div className="text-sm text-[#412402]/60 space-y-1">
                                <p><span className="font-medium">اسم المستخدم:</span> @{student.username}</p>
                                {student.date_of_birth && (
                                  <p><span className="font-medium">تاريخ الميلاد:</span> {new Date(student.date_of_birth).toLocaleDateString('ar-SA')}</p>
                                )}
                                {student.phone_number && (
                                  <p><span className="font-medium">رقم الهاتف:</span> {student.phone_number}</p>
                                )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="text-sm text-[#412402]/60 space-y-1">
                                <p><span className="font-medium">المدرسة:</span> {student.school || '-'}</p>
                                <p><span className="font-medium">الصف:</span> {student.grade}</p>
                                <p><span className="font-medium">الشعبة:</span> {student.section}</p>
                                <p className="text-xs text-[#412402]/50">
                                  تاريخ التسجيل: {new Date(student.created_at).toLocaleString('ar-SA')}
                                </p>
                              </div>
                            </div>
                          </div>
                          {/* Action Buttons */}
                          <div className="flex items-center gap-2 pt-2 border-t border-amber-200">
                            <Button 
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => {
                                setSelectedStudent(student);
                                setApprovalDialogOpen(true);
                              }}
                              disabled={processingStudent === student.id}
                              data-testid={`approve-student-btn-${student.id}`}
                            >
                              {processingStudent === student.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 ml-1" />
                                  قبول
                                </>
                              )}
                            </Button>
                            <Button 
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setSelectedStudent(student);
                                setRejectionDialogOpen(true);
                              }}
                              disabled={processingStudent === student.id}
                              data-testid={`reject-student-btn-${student.id}`}
                            >
                              <XCircle className="w-4 h-4 ml-1" />
                              رفض
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Students */}
            <Card className="border-[#E8DFCF]">
              <CardHeader className="border-b border-[#E8DFCF]">
                <CardTitle className="text-lg font-semibold text-[#412402] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#412402]/60" />
                  جميع الطلاب ({filteredAllStudents.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {filteredAllStudents.length === 0 ? (
                  <div className="py-12 text-center">
                    <Users className="w-12 h-12 text-[#412402]/30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-[#412402]/80 mb-2">لا يوجد طلاب</h3>
                    <p className="text-[#412402]/50">لم يسجل أي طالب بعد</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                      <thead>
                        <tr className="bg-[#FFFFFF] border-b border-[#E8DFCF]">
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap">الاسم الرباعي</th>
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap">اسم المستخدم</th>
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap hidden md:table-cell">تاريخ الميلاد</th>
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap hidden lg:table-cell">الهاتف</th>
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap hidden lg:table-cell">المدرسة</th>
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap">الصف / الشعبة</th>
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap">الحالة</th>
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap">الوقت على المنصة</th>
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap hidden sm:table-cell">تاريخ التسجيل</th>
                          <th className="px-3 py-3 text-right text-xs sm:text-sm font-semibold text-[#412402]/80 whitespace-nowrap">إجراءات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAllStudents.map((student) => (
                          <tr key={student.id} className="border-b border-[#E8DFCF] hover:bg-[#FFFFFF]">
                            <td className="px-3 py-3 font-medium text-[#412402] text-sm min-w-[160px]">
                              <span className="flex items-center gap-2">
                                {onlineStudentIds.has(student.id) && (
                                  <span className="relative flex h-2.5 w-2.5 shrink-0" title="متصل الآن">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                                  </span>
                                )}
                                {student.full_name || student.name}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-[#412402]/60 text-xs sm:text-sm break-all max-w-[120px]">@{student.username}</td>
                            <td className="px-3 py-3 text-[#412402]/60 text-sm hidden md:table-cell whitespace-nowrap">
                              {student.date_of_birth 
                                ? new Date(student.date_of_birth).toLocaleDateString('ar-SA')
                                : '-'
                              }
                            </td>
                            <td className="px-3 py-3 text-[#412402]/60 text-sm hidden lg:table-cell" dir="ltr">
                              {student.phone_number || '-'}
                            </td>
                            <td className="px-3 py-3 text-[#412402]/60 text-sm hidden lg:table-cell max-w-[100px] truncate">{student.school || '-'}</td>
                            <td className="px-3 py-3 text-[#412402]/60 text-xs sm:text-sm whitespace-nowrap">
                              {student.grade} / {student.section}
                            </td>
                            <td className="px-3 py-3">{getStatusBadge(student.status)}</td>
                            <td className="px-3 py-3 text-xs whitespace-nowrap">
                              {student.total_active_seconds ? (
                                <span className="inline-flex items-center gap-1 text-[#412402]/70" title="إجمالي الوقت الذي قضاه الطالب داخل المنصة">
                                  <Clock className="w-3.5 h-3.5 text-amber-700" />
                                  {fmtDuration(student.total_active_seconds)}
                                </span>
                              ) : (
                                <span className="text-[#412402]/30">—</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-xs text-[#412402]/50 hidden sm:table-cell whitespace-nowrap">
                              {new Date(student.created_at).toLocaleDateString('ar-SA')}
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-1">
                                {/* Edit Student Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                                  onClick={() => openEditStudentDialog(student)}
                                  title="تعديل البيانات"
                                  data-testid={`edit-student-btn-${student.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {/* View All Attempts Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-8 w-8 p-0"
                                  onClick={() => navigate(`/teacher/students/${student.id}/attempts`)}
                                  title="عرض جميع المحاولات"
                                  data-testid={`attempts-student-btn-${student.id}`}
                                >
                                  <Layers className="w-4 h-4" />
                                </Button>
                                {/* Deactivate/Reactivate Button */}
                                {student.status === 'approved' ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 w-8 p-0"
                                    onClick={() => handleDeactivateStudent(student)}
                                    title="إلغاء التفعيل"
                                    data-testid={`deactivate-student-btn-${student.id}`}
                                  >
                                    <UserX className="w-4 h-4" />
                                  </Button>
                                ) : (student.status === 'deactivated' || student.status === 'rejected') && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 w-8 p-0"
                                    onClick={() => handleReactivateStudent(student)}
                                    title="إعادة التفعيل"
                                    data-testid={`reactivate-student-btn-${student.id}`}
                                  >
                                    <UserCheck className="w-4 h-4" />
                                  </Button>
                                )}
                                {/* Delete Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                  onClick={() => {
                                    setStudentToDelete(student);
                                    setDeleteStudentDialogOpen(true);
                                  }}
                                  data-testid={`delete-student-btn-${student.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Violations Tab */}
          <TabsContent value="violations">
            <Card className="border-[#E8DFCF]">
              <CardHeader className="border-b border-[#E8DFCF]">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="text-lg font-semibold text-[#412402] flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    سجل المخالفات ({filteredViolations.length})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchFilteredViolations}
                    >
                      <RefreshCw className="w-4 h-4 ml-1" />
                      تحديث
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setResetViolationsDialogOpen(true)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      data-testid="reset-violations-btn"
                    >
                      <Trash2 className="w-4 h-4 ml-1" />
                      تصفير المخالفات
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {/* Filters */}
                <div className="grid sm:grid-cols-3 gap-4 mb-6 p-4 bg-[#FFFFFF] rounded-lg">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-[#412402]/80">فلترة حسب الاختبار</Label>
                    <Select
                      value={violationFilters.examId}
                      onValueChange={(value) => setViolationFilters(prev => ({ ...prev, examId: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="جميع الاختبارات" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">جميع الاختبارات</SelectItem>
                        {exams.map((exam) => (
                          <SelectItem key={exam.id} value={exam.id}>
                            {exam.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-[#412402]/80">فلترة حسب الطالب</Label>
                    <Select
                      value={violationFilters.studentId}
                      onValueChange={(value) => setViolationFilters(prev => ({ ...prev, studentId: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="جميع الطلاب" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">جميع الطلاب</SelectItem>
                        {[...new Set(violations.map(v => JSON.stringify({ id: v.student_id, name: v.student_name })))].map((s) => {
                          const student = JSON.parse(s);
                          return (
                            <SelectItem key={student.id} value={student.id}>
                              {student.name}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-[#412402]/80">فلترة حسب النوع</Label>
                    <Select
                      value={violationFilters.violationType}
                      onValueChange={(value) => setViolationFilters(prev => ({ ...prev, violationType: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="جميع الأنواع" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">جميع الأنواع</SelectItem>
                        {violationTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {getViolationTypeLabel(type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Student Summary */}
                {violationSummary.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-[#412402]/80 mb-3">ملخص المخالفات حسب الطالب</h4>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {violationSummary.slice(0, 6).map((summary) => (
                        <div key={summary.student_id} className="bg-red-50 rounded-lg p-3 border border-red-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-[#412402]">{summary.student_name}</span>
                            <Badge variant="destructive">{summary.total_count} مخالفات</Badge>
                          </div>
                          <div className="text-xs text-[#412402]/60">
                            <p>أول مخالفة: {new Date(summary.first_occurrence).toLocaleDateString('ar-SA')}</p>
                            <p>آخر مخالفة: {new Date(summary.last_occurrence).toLocaleDateString('ar-SA')}</p>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {Object.entries(summary.by_type || {}).map(([type, count]) => (
                              <Badge key={type} variant="outline" className="text-xs">
                                {getViolationTypeLabel(type)}: {count}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Violations List */}
                {filteredViolations.length === 0 ? (
                  <div className="py-12 text-center">
                    <AlertTriangle className="w-12 h-12 text-[#412402]/30 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-[#412402]/80 mb-2">لا توجد مخالفات</h3>
                    <p className="text-[#412402]/50">لم يتم تسجيل أي مخالفات بعد</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 border rounded-lg">
                    {filteredViolations.map((violation) => (
                      <div key={violation.id} className="p-4 hover:bg-[#FFFFFF]">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-[#412402]">{violation.student_name}</p>
                            <p className="text-sm text-[#412402]/60">{violation.exam_title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {getViolationTypeLabel(violation.violation_type)}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-left">
                            <Badge variant="destructive">مخالفة {violation.violation_number}</Badge>
                            <p className="text-xs text-[#412402]/50 mt-1">
                              {new Date(violation.timestamp).toLocaleString('ar-SA')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* News Ticker Tab */}
          <TabsContent value="news">
            <div className="space-y-6">
              <Card className="border-[#E8DFCF]">
                <CardHeader className="border-b border-[#E8DFCF]">
                  <CardTitle className="text-lg font-semibold text-[#412402] flex items-center gap-2">
                    <Radio className="w-5 h-5 text-[#EF9F27]" />
                    {editingNewsId ? 'تعديل خبر' : 'إضافة خبر جديد'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#412402]">محتوى الخبر</Label>
                    <Textarea
                      placeholder="اكتب نص الخبر هنا... سيظهر كشريط متحرك أسفل الصفحة للطلاب"
                      rows={3}
                      value={newsForm.content}
                      onChange={e => setNewsForm(p => ({ ...p, content: e.target.value }))}
                      className="border-[#E8DFCF]"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#412402]">يظهر لـ</Label>
                      <Select value={newsForm.grade} onValueChange={v => setNewsForm(p => ({ ...p, grade: v }))}>
                        <SelectTrigger className="border-[#E8DFCF]">
                          <SelectValue placeholder="اختر الصف" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">جميع الطلاب</SelectItem>
                          {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-3">
                      <Button
                        onClick={handleSaveNews}
                        disabled={savingNews}
                        className="bg-[#EF9F27] hover:bg-[#D88E1F] flex-1"
                      >
                        {savingNews ? <Loader2 className="w-4 h-4 animate-spin" /> : editingNewsId ? 'حفظ التعديل' : 'إضافة الخبر'}
                      </Button>
                      {editingNewsId && (
                        <Button variant="outline" onClick={() => { setEditingNewsId(null); setNewsForm({ content: '', grade: 'all', is_active: true }); }}>
                          إلغاء
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* News Items List */}
              <Card className="border-[#E8DFCF]">
                <CardHeader className="border-b border-[#E8DFCF]">
                  <CardTitle className="text-base font-semibold text-[#412402]">
                    الأخبار المضافة ({newsItems.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {newsItems.length === 0 ? (
                    <div className="py-10 text-center text-[#412402]/50">
                      <Newspaper className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>لا توجد أخبار بعد. أضف خبراً ليظهر للطلاب.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-[#E8DFCF]">
                      {newsItems.map(item => (
                        <div key={item.id} className="p-4 flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm text-[#412402] ${!item.is_active ? 'line-through opacity-50' : ''}`}>
                              {item.content}
                            </p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-[#412402]/50">
                                {new Date(item.created_at).toLocaleDateString('ar-SA')}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {item.grade === 'all' ? 'جميع الطلاب' : item.grade}
                              </Badge>
                              <Badge className={`text-xs ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                {item.is_active ? 'مفعّل' : 'معطّل'}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleNews(item)}
                              title={item.is_active ? 'تعطيل' : 'تفعيل'}
                              className="text-slate-500"
                            >
                              {item.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingNewsId(item.id);
                                setNewsForm({ content: item.content, grade: item.grade, is_active: item.is_active });
                              }}
                              className="text-blue-600"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteNews(item.id)}
                              className="text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

        </Tabs>
      </main>

      {/* Floating support chat (replaces the old "المحادثات" tab) */}
      {can('chat') && <FloatingChat unread={dmUnread} title="المحادثات" panelClassName="max-w-[760px] h-[78vh]">
        <TeacherMessages apiUrl={API_URL} token={token} compact />
      </FloatingChat>}

      {/* Delete Exam Dialog */}
      {/* Export Exam Questions To Bank Dialog */}
      {exportBankOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <CardHeader className="border-b">
              <CardTitle className="text-lg">تصدير أسئلة الاختبار إلى بنك الأسئلة</CardTitle>
              <CardDescription className="text-xs">
                {exportBankExam?.title} — اختر الموضوع والأسئلة المراد نسخها
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1 space-y-3 py-3">
              <div className="space-y-1">
                <Label className="text-sm">الموضوع المستهدف في بنك الأسئلة</Label>
                <Select value={exportBankTopicId} onValueChange={setExportBankTopicId}>
                  <SelectTrigger><SelectValue placeholder="اختر موضوعاً..." /></SelectTrigger>
                  <SelectContent>
                    {topics.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-500">لا توجد مواضيع — أنشئ موضوعاً أولاً</div>
                    ) : topics.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.title} ({t.grade})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">الأسئلة ({Object.values(exportBankSelectedIds).filter(Boolean).length} / {exportBankExam?.questions?.length || 0})</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const allOn = (exportBankExam?.questions || []).every(q => exportBankSelectedIds[q.id]);
                    if (allOn) setExportBankSelectedIds({});
                    else {
                      const m = {};
                      (exportBankExam?.questions || []).forEach(q => { m[q.id] = true; });
                      setExportBankSelectedIds(m);
                    }
                  }}
                >
                  {(exportBankExam?.questions || []).every(q => exportBankSelectedIds[q.id]) ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                </Button>
              </div>
              <div className="space-y-2">
                {(exportBankExam?.questions || []).map((q, idx) => (
                  <label
                    key={q.id}
                    className={`flex items-start gap-3 p-2 rounded border ${exportBankSelectedIds[q.id] ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!exportBankSelectedIds[q.id]}
                      onChange={() => setExportBankSelectedIds(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-500 mb-1">#{idx + 1} • {q.difficulty || 'medium'} • {q.points || 1} نقطة</div>
                      <div className="text-sm font-medium text-[#412402]">{q.text}</div>
                    </div>
                  </label>
                ))}
              </div>
            </CardContent>
            <div className="p-4 border-t flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setExportBankOpen(false)} disabled={exportingToBank}>إلغاء</Button>
              <Button onClick={submitExportToBank} disabled={exportingToBank} className="bg-amber-700 hover:bg-amber-800">
                {exportingToBank ? 'جاري النقل...' : 'نقل إلى بنك الأسئلة'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <AlertDialog open={resetViolationsDialogOpen} onOpenChange={setResetViolationsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تصفير المخالفات؟</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف سجلات المخالفات
              {violationFilters.examId !== 'all' ? ' للاختبار المختار' : ''}
              {violationFilters.studentId !== 'all' ? ' للطالب المختار' : ''}
              {violationFilters.examId === 'all' && violationFilters.studentId === 'all' ? ' لجميع الاختبارات والطلاب' : ''}
              ، وإعادة عدّاد المخالفات في الجلسات إلى الصفر. لا يمكن التراجع.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={resettingViolations}>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                setResettingViolations(true);
                try {
                  const params = new URLSearchParams();
                  if (violationFilters.examId !== 'all') params.append('exam_id', violationFilters.examId);
                  if (violationFilters.studentId !== 'all') params.append('student_id', violationFilters.studentId);
                  const r = await fetch(`${API_URL}/api/teacher/violations/reset?${params.toString()}`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                  });
                  if (r.ok) {
                    toast.success('تم تصفير المخالفات');
                    setResetViolationsDialogOpen(false);
                    fetchFilteredViolations();
                  } else {
                    toast.error('فشل التصفير');
                  }
                } catch {
                  toast.error('حدث خطأ أثناء التصفير');
                }
                setResettingViolations(false);
              }}
              disabled={resettingViolations}
              className="bg-red-600 hover:bg-red-700"
            >
              {resettingViolations ? 'جاري التصفير...' : 'تصفير'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>هل أنت متأكد من حذف الاختبار؟</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف الاختبار نهائياً ولن تتمكن من استرجاعه. سيتم أيضاً حذف جميع النتائج المرتبطة به.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteExam}
              className="bg-red-600 hover:bg-red-700"
            >
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve Student Dialog */}
      <AlertDialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تفعيل حساب الطالب</AlertDialogTitle>
            <AlertDialogDescription>
              هل تريد تفعيل حساب الطالب <strong>{selectedStudent?.name}</strong>؟
              <br />
              سيتمكن الطالب من الوصول إلى الاختبارات بعد التفعيل.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleApproveStudent}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 ml-2" />
              تفعيل الحساب
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Student Dialog */}
      <AlertDialog open={rejectionDialogOpen} onOpenChange={setRejectionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>رفض طلب التسجيل</AlertDialogTitle>
            <AlertDialogDescription>
              هل تريد رفض طلب الطالب <strong>{selectedStudent?.name}</strong>؟
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="سبب الرفض (اختياري)..."
              value={rejectionNote}
              onChange={(e) => setRejectionNote(e.target.value)}
              rows={3}
            />
          </div>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => setRejectionNote('')}>إلغاء</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRejectStudent}
              className="bg-red-600 hover:bg-red-700"
            >
              <XCircle className="w-4 h-4 ml-2" />
              رفض الطلب
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Student Dialog */}
      <AlertDialog open={deleteStudentDialogOpen} onOpenChange={setDeleteStudentDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              حذف الطالب
            </AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف الطالب <strong>{studentToDelete?.full_name || studentToDelete?.username}</strong>؟
              <br />
              <span className="text-red-600 font-medium">سيتم حذف جميع بيانات الطالب بما في ذلك نتائج الاختبارات.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => setStudentToDelete(null)}>
              إلغاء
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteStudent}
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingStudent}
            >
              {deletingStudent ? (
                <>
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  جاري الحذف...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 ml-2" />
                  حذف الطالب
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Secret Question Dialog */}
      <AlertDialog open={secretQuestionDialogOpen} onOpenChange={setSecretQuestionDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right">
              {hasSecretQuestion ? 'تعديل السؤال السري' : 'تعيين السؤال السري'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              هذا السؤال سيُستخدم للتحقق من هوية الطلاب عند استعادة كلمة المرور. 
              احتفظ بالإجابة سراً ولا تشاركها إلا مع الطلاب الذين يحتاجون لاستعادة كلمة مرورهم.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="secret_question" className="text-[#412402]/80">السؤال السري</Label>
              <Input
                id="secret_question"
                placeholder="مثال: ما هو اسم المدرسة؟"
                value={secretQuestion}
                onChange={(e) => setSecretQuestion(e.target.value)}
                data-testid="secret-question-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secret_answer" className="text-[#412402]/80">الإجابة السرية</Label>
              <Input
                id="secret_answer"
                type="password"
                placeholder="ادخل الإجابة السرية"
                value={secretAnswer}
                onChange={(e) => setSecretAnswer(e.target.value)}
                data-testid="secret-answer-input"
              />
              <p className="text-xs text-[#412402]/50">
                سيتم تشفير الإجابة ولن تظهر لأي شخص بما فيهم أنت
              </p>
            </div>
          </div>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogCancel disabled={savingSecret}>إلغاء</AlertDialogCancel>
            <Button
              onClick={handleSaveSecretQuestion}
              disabled={savingSecret || !secretQuestion || !secretAnswer}
              className="bg-amber-700 hover:bg-amber-800"
              data-testid="save-secret-btn"
            >
              {savingSecret ? (
                <>
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  جاري الحفظ...
                </>
              ) : (
                'حفظ'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recovery Codes Dialog */}
      <Dialog open={recoveryCodesDialogOpen} onOpenChange={(open) => { setRecoveryCodesDialogOpen(open); if (!open) setGeneratedCodes([]); }}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              رموز الاستعادة الاحتياطية
            </DialogTitle>
            <DialogDescription>
              {generatedCodes.length === 0
                ? recoveryCodesRemaining > 0
                  ? `لديك ${recoveryCodesRemaining} رمز متبقٍ. توليد رموز جديدة سيلغي الرموز القديمة.`
                  : 'ولّد مجموعة رموز احتياطية لاستخدامها إذا نسيت كلمة المرور والسؤال السري.'
                : 'احفظ هذه الرموز في مكان آمن. كل رمز للاستخدام مرة واحدة فقط ولن تُعرض مجدداً.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {generatedCodes.length === 0 ? (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  <p className="font-semibold mb-1">تنبيهات مهمة:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>سيتم إلغاء أي رموز سابقة عند التوليد</li>
                    <li>كل رمز يُستخدم مرة واحدة فقط لإعادة تعيين كلمة المرور</li>
                    <li>لن تتمكن من رؤية الرموز مجدداً بعد إغلاق هذه النافذة</li>
                    <li>احفظها في مكان آمن (ورقة، مدير كلمات مرور)</li>
                  </ul>
                </div>
                <Button
                  onClick={handleGenerateRecoveryCodes}
                  disabled={generatingCodes}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {generatingCodes ? <><Loader2 className="w-4 h-4 ml-2 animate-spin" />جاري التوليد...</> : <><RefreshCw className="w-4 h-4 ml-2" />توليد 8 رموز احتياطية</>}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 font-medium text-center">
                  تم توليد الرموز بنجاح — احفظها الآن
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {generatedCodes.map((code, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-100 rounded-md px-3 py-2">
                      <span className="font-mono text-sm font-bold text-slate-800 tracking-wider">{code}</span>
                      <button
                        onClick={() => { navigator.clipboard.writeText(code); toast.success('تم النسخ'); }}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const text = generatedCodes.join('\n');
                    const blob = new Blob([`رموز الاستعادة الاحتياطية\n\n${text}\n\nملاحظة: كل رمز للاستخدام مرة واحدة فقط.`], { type: 'text/plain;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'recovery-codes.txt'; a.click(); URL.revokeObjectURL(url);
                  }}
                >
                  <Download className="w-4 h-4 ml-2" />
                  تنزيل الرموز كملف نصي
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRecoveryCodesDialogOpen(false); setGeneratedCodes([]); }}>إغلاق</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visibility Settings Dialog */}
      <Dialog open={visibilityDialogOpen} onOpenChange={setVisibilityDialogOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#EF9F27]" />
              إعدادات ظهور النتائج للطلاب
            </DialogTitle>
            <DialogDescription>
              {selectedExamForVisibility?.title} — التغييرات تؤثر فوراً على الطلاب الذين أنهوا الاختبار
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {[
              { key: 'show_score_to_student', label: 'إظهار الدرجة الكلية' },
              { key: 'show_percentage', label: 'إظهار النسبة المئوية' },
              { key: 'show_pass_fail_only', label: 'ناجح / راسب فقط (بدون درجة)' },
              { key: 'show_correct_answers', label: 'إظهار الإجابات الصحيحة' },
              { key: 'show_wrong_answers', label: 'إظهار الإجابات الخاطئة فقط' },
              { key: 'show_answer_review', label: 'السماح بمراجعة الإجابات سؤالاً بسؤال' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <Label className="text-sm text-[#412402]/80">{label}</Label>
                <Switch
                  checked={!!visibilitySettings[key]}
                  onCheckedChange={(val) =>
                    setVisibilitySettings(prev => ({ ...prev, [key]: val }))
                  }
                />
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 flex-row-reverse">
            <Button variant="outline" onClick={() => setVisibilityDialogOpen(false)}>إلغاء</Button>
            <Button
              onClick={handleSaveVisibility}
              disabled={savingVisibility}
              className="bg-[#EF9F27] hover:bg-[#D88E1F]"
            >
              {savingVisibility ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              حفظ الإعدادات
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Student Dialog */}
      <Dialog open={editStudentDialogOpen} onOpenChange={setEditStudentDialogOpen}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              تعديل بيانات الطالب
            </DialogTitle>
            <DialogDescription>
              {studentToEdit?.full_name || studentToEdit?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">الاسم الأول</Label>
              <Input value={editStudentForm.first_name || ''} onChange={e => setEditStudentForm(p => ({ ...p, first_name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">اسم الأب</Label>
              <Input value={editStudentForm.second_name || ''} onChange={e => setEditStudentForm(p => ({ ...p, second_name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">اسم الجد</Label>
              <Input value={editStudentForm.third_name || ''} onChange={e => setEditStudentForm(p => ({ ...p, third_name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">اسم العائلة</Label>
              <Input value={editStudentForm.family_name || ''} onChange={e => setEditStudentForm(p => ({ ...p, family_name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">اسم المستخدم</Label>
              <Input value={editStudentForm.username || ''} onChange={e => setEditStudentForm(p => ({ ...p, username: e.target.value }))} dir="ltr" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">رقم الهاتف</Label>
              <Input value={editStudentForm.phone_number || ''} onChange={e => setEditStudentForm(p => ({ ...p, phone_number: e.target.value }))} dir="ltr" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">تاريخ الميلاد</Label>
              <Input type="date" value={editStudentForm.date_of_birth || ''} onChange={e => setEditStudentForm(p => ({ ...p, date_of_birth: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">المدرسة</Label>
              <Input value={editStudentForm.school || ''} onChange={e => setEditStudentForm(p => ({ ...p, school: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">الصف</Label>
              <Select value={editStudentForm.grade} onValueChange={v => setEditStudentForm(p => ({ ...p, grade: v }))}>
                <SelectTrigger><SelectValue placeholder="اختر الصف" /></SelectTrigger>
                <SelectContent>
                  {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#412402]/70">الشعبة</Label>
              <Select value={editStudentForm.section} onValueChange={v => setEditStudentForm(p => ({ ...p, section: v }))}>
                <SelectTrigger><SelectValue placeholder="اختر الشعبة" /></SelectTrigger>
                <SelectContent>
                  {sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 flex-row-reverse">
            <Button variant="outline" onClick={() => setEditStudentDialogOpen(false)}>إلغاء</Button>
            <Button
              onClick={handleSaveEditStudent}
              disabled={savingStudent}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingStudent ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              حفظ التغييرات
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Topic Dialog */}
      <Dialog open={topicDialogOpen} onOpenChange={setTopicDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">موضوع جديد</DialogTitle>
            <DialogDescription className="text-right">
              أنشئ مجلداً للموضوع. ستضيف الأسئلة بعد الإنشاء.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>عنوان الموضوع <span className="text-red-500">*</span></Label>
              <Input
                value={topicForm.title}
                onChange={(e) => setTopicForm({ ...topicForm, title: e.target.value })}
                placeholder="مثال: اسم الفاعل"
                data-testid="topic-title-input"
              />
            </div>
            <div>
              <Label>الوصف</Label>
              <Textarea
                value={topicForm.description}
                onChange={(e) => setTopicForm({ ...topicForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                <Folder className="w-4 h-4 text-amber-700" />
                المجلد / التصنيف
              </Label>
              <Input
                value={topicForm.category}
                onChange={(e) => setTopicForm({ ...topicForm, category: e.target.value })}
                placeholder="مثال: النحو، البلاغة، الإملاء (اختياري)"
                list="topic-categories"
                data-testid="topic-category-input"
              />
              <datalist id="topic-categories">
                {[...new Set(topics.map(t => t.category).filter(Boolean))].map(c => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <p className="text-xs text-[#412402]/50 mt-1">المواضيع التي لها نفس التصنيف ستجمع في مجلد — استخدم "/" للمجلدات المتداخلة (مثال: النحو/الجملة الاسمية)</p>
            </div>
            {/* Everything else (الصف، الشعبة، توزيع الصعوبة، عدد الأسئلة، المدة،
                السعر، عدد المحاولات…) is configured ON THE FOLDER. */}
            <div className="border-t pt-3">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                <Banknote className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
                <span>
                  <b>الصف</b> و<b>الشعبة</b> و<b>توزيع الصعوبة</b> و<b>عدد الأسئلة</b> و<b>مدة الاختبار</b> و<b>السعر</b> و<b>عدد المحاولات</b> تُحدَّد جميعها على مستوى <b>المجلد</b> (يشتري الطالب المجلد كاملاً). بعد الإنشاء افتح المجلد في مستكشف بنك الأسئلة واضغط «إعدادات المجلد».
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopicDialogOpen(false)} disabled={savingTopic}>إلغاء</Button>
            <Button
              onClick={handleCreateTopic}
              disabled={savingTopic}
              className="bg-amber-700 hover:bg-amber-800"
              data-testid="save-topic-btn"
            >
              {savingTopic ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              إنشاء الموضوع
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Topic Confirmation */}
      <AlertDialog open={deleteTopicDialogOpen} onOpenChange={setDeleteTopicDialogOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right">حذف الموضوع</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              سيتم حذف الموضوع "{topicToDelete?.title}" مع كل الأسئلة والمحاولات السابقة. هذا الإجراء لا يمكن التراجع عنه.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => setTopicToDelete(null)}>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTopic} className="bg-red-600 hover:bg-red-700">
              حذف نهائي
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============== TEACHER QUESTION BANK (Windows 11 File Explorer style) ==============
function buildTopicTree(topics) {
  const root = { children: {}, topics: [] };
  for (const topic of topics) {
    const raw = (topic.category || '').trim();
    const parts = raw ? raw.split('/').map(p => p.trim()).filter(Boolean) : [];
    if (parts.length === 0) {
      root.topics.push(topic);
    } else {
      let node = root;
      for (const part of parts) {
        if (!node.children[part]) node.children[part] = { children: {}, topics: [] };
        node = node.children[part];
      }
      node.topics.push(topic);
    }
  }
  return root;
}

function countTreeTopics(node) {
  return node.topics.length + Object.values(node.children).reduce((s, c) => s + countTreeTopics(c), 0);
}

function getNodeAtPath(root, parts) {
  let node = root;
  for (const p of parts) {
    if (!node || !node.children[p]) return null;
    node = node.children[p];
  }
  return node;
}

function ensureFolderPath(root, parts) {
  let node = root;
  for (const p of parts) {
    if (!node.children[p]) node.children[p] = { children: {}, topics: [] };
    node = node.children[p];
  }
  return node;
}


// Windows 11–style folder icon (yellow manila folder, drawn with SVG so it
// looks the same regardless of which lucide icons are present).
const Win11FolderIcon = ({ size = 56 }) => (
  <svg viewBox="0 0 64 52" width={size} height={(size * 52) / 64} aria-hidden="true">
    <defs>
      <linearGradient id="w11FolderBack" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F2C14E" />
        <stop offset="100%" stopColor="#D9A12F" />
      </linearGradient>
      <linearGradient id="w11FolderFront" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFD66B" />
        <stop offset="100%" stopColor="#E8B23F" />
      </linearGradient>
    </defs>
    <path d="M4 10 q0 -4 4 -4 h16 l4 4 h28 q4 0 4 4 v30 q0 4 -4 4 h-48 q-4 0 -4 -4 z" fill="url(#w11FolderBack)" />
    <path d="M2 18 q0 -3 3 -3 h54 q3 0 3 3 v26 q0 4 -4 4 h-52 q-4 0 -4 -4 z" fill="url(#w11FolderFront)" />
    <path d="M2 18 q0 -3 3 -3 h54 q3 0 3 3 v2 h-60 z" fill="#FFFFFF" opacity="0.25" />
  </svg>
);

const Win11AddressBar = ({ pathParts, onNavigate, onBack, onForward, onUp, canBack, canForward, canUp, search, onSearchChange, onDropPath }) => (
  <div className="flex flex-wrap items-center gap-2 bg-white/70 backdrop-blur border border-[#E5E5E5] rounded-xl px-3 py-2 shadow-sm">
    <div className="flex items-center gap-1">
      <button onClick={onBack} disabled={!canBack} title="رجوع"
        className="w-8 h-8 rounded-lg flex items-center justify-center text-[#412402] hover:bg-black/5 disabled:opacity-30 disabled:hover:bg-transparent">
        <ArrowRightIcon className="w-4 h-4" />
      </button>
      <button onClick={onForward} disabled={!canForward} title="تقدم"
        className="w-8 h-8 rounded-lg flex items-center justify-center text-[#412402] hover:bg-black/5 disabled:opacity-30 disabled:hover:bg-transparent">
        <ArrowLeftIcon className="w-4 h-4" />
      </button>
      <button onClick={onUp} disabled={!canUp} title="مجلد أعلى"
        className="w-8 h-8 rounded-lg flex items-center justify-center text-[#412402] hover:bg-black/5 disabled:opacity-30 disabled:hover:bg-transparent">
        <ArrowUp className="w-4 h-4" />
      </button>
    </div>

    <div className="flex-1 min-w-[200px] flex items-center gap-1 bg-white border border-[#E5E5E5] rounded-lg px-2 py-1.5 text-sm" dir="rtl">
      <button
        onClick={() => onNavigate([])}
        onDragOver={(e) => { if (onDropPath) e.preventDefault(); }}
        onDrop={() => onDropPath?.('')}
        className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-amber-50 text-[#412402] font-medium"
        title="اسحب هنا للنقل إلى الجذر"
      >
        <Home className="w-3.5 h-3.5 text-amber-700" />
        بنك الأسئلة
      </button>
      {pathParts.map((part, idx) => (
        <React.Fragment key={idx}>
          <ChevronLeft className="w-3.5 h-3.5 text-[#412402]/40" />
          <button
            onClick={() => onNavigate(pathParts.slice(0, idx + 1))}
            onDragOver={(e) => { if (onDropPath) e.preventDefault(); }}
            onDrop={() => onDropPath?.(pathParts.slice(0, idx + 1).join('/'))}
            className="px-1.5 py-0.5 rounded hover:bg-amber-50 text-[#412402] truncate max-w-[160px]"
            title={part}
          >
            {part}
          </button>
        </React.Fragment>
      ))}
    </div>

    <div className="flex items-center gap-1 bg-white border border-[#E5E5E5] rounded-lg px-2 py-1.5 min-w-[180px]">
      <Search className="w-4 h-4 text-[#412402]/40" />
      <input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="بحث في هذا المجلد"
        className="bg-transparent outline-none text-sm flex-1 text-[#412402] placeholder:text-[#412402]/40"
      />
    </div>
  </div>
);

const Win11Toolbar = ({ onCreateTopic, onCreateFolder, view, onViewChange, selectedTopic, onManage, onDelete, onMove, onMoveFolder, selectedFolderName }) => (
  <div className="flex flex-wrap items-center gap-1 bg-white/70 backdrop-blur border border-[#E5E5E5] rounded-xl px-2 py-1.5 shadow-sm">
    <button
      onClick={onCreateFolder}
      data-testid="new-folder-btn"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 font-semibold"
    >
      <Folder className="w-4 h-4 text-amber-700" /> مجلد جديد
    </button>
    <button onClick={onCreateTopic} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-[#412402] hover:bg-amber-50">
      <Plus className="w-4 h-4 text-amber-700" /> موضوع جديد
    </button>
    <div className="w-px h-5 bg-[#E5E5E5] mx-1" />
    <button
      disabled={!selectedTopic}
      onClick={() => selectedTopic && onManage(selectedTopic)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-[#412402] hover:bg-amber-50 disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <Edit className="w-4 h-4" /> إدارة
    </button>
    <button
      disabled={!selectedTopic}
      onClick={() => selectedTopic && onMove(selectedTopic)}
      data-testid="move-topic-btn"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-[#412402] hover:bg-amber-50 disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <FolderOpen className="w-4 h-4 text-amber-700" /> نقل موضوع
    </button>
    <button
      disabled={!selectedFolderName}
      onClick={() => selectedFolderName && onMoveFolder(selectedFolderName)}
      data-testid="move-folder-btn"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-[#412402] hover:bg-amber-50 disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <Folder className="w-4 h-4 text-amber-700" /> نقل مجلد
    </button>
    <button
      disabled={!selectedTopic && !selectedFolderName}
      onClick={() => onDelete(selectedTopic)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <Trash2 className="w-4 h-4" /> حذف
    </button>
    <div className="flex-1" />
    <div className="flex items-center bg-white border border-[#E5E5E5] rounded-lg p-0.5">
      <button
        onClick={() => onViewChange('grid')}
        className={`w-8 h-7 rounded-md flex items-center justify-center ${view === 'grid' ? 'bg-amber-100 text-amber-800' : 'text-[#412402]/60 hover:bg-black/5'}`}
        title="عرض شبكي"
      >
        <LayoutGrid className="w-4 h-4" />
      </button>
      <button
        onClick={() => onViewChange('list')}
        className={`w-8 h-7 rounded-md flex items-center justify-center ${view === 'list' ? 'bg-amber-100 text-amber-800' : 'text-[#412402]/60 hover:bg-black/5'}`}
        title="عرض قائمة"
      >
        <ListIcon className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const Win11Sidebar = ({ tree, currentPath, onNavigate }) => {
  const renderNode = (node, parts, depth) => {
    const names = Object.keys(node.children).sort((a, b) => a.localeCompare(b, 'ar'));
    return names.map((name) => {
      const childPath = [...parts, name];
      const isActive = childPath.join('/') === currentPath.join('/');
      const isAncestor = currentPath.length >= childPath.length &&
        childPath.every((p, i) => currentPath[i] === p);
      return (
        <div key={childPath.join('/')}>
          <button
            onClick={() => onNavigate(childPath)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-right ${
              isActive ? 'bg-amber-100 text-amber-900 font-semibold' : 'text-[#412402] hover:bg-black/5'
            }`}
            style={{ paddingRight: 8 + depth * 12 }}
          >
            {isAncestor && Object.keys(node.children[name].children).length > 0
              ? <FolderOpen className="w-4 h-4 text-amber-600" />
              : <Folder className="w-4 h-4 text-amber-600" />}
            <span className="truncate">{name}</span>
          </button>
          {isAncestor && renderNode(node.children[name], childPath, depth + 1)}
        </div>
      );
    });
  };

  return (
    <aside className="w-56 shrink-0 bg-white/70 backdrop-blur border border-[#E5E5E5] rounded-xl p-2 hidden md:block max-h-[70vh] overflow-y-auto">
      <button
        onClick={() => onNavigate([])}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-right ${
          currentPath.length === 0 ? 'bg-amber-100 text-amber-900 font-semibold' : 'text-[#412402] hover:bg-black/5'
        }`}
      >
        <Home className="w-4 h-4 text-amber-700" />
        <span>بنك الأسئلة</span>
      </button>
      <div className="mt-1">{renderNode(tree, [], 0)}</div>
    </aside>
  );
};

const Win11FolderTile = ({ name, count, selected, renaming, renameValue, onRenameChange, onRenameCommit, onRenameCancel, onSelect, onOpen,
  draggable, onDragStart, onDragEnd, onDragOverFolder, onDropFolder, dropActive, price, onSettings, onStartRename }) => (
  <div
    onClick={onSelect}
    onDoubleClick={() => { if (!renaming) onOpen(); }}
    draggable={draggable && !renaming}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onDragOver={(e) => { if (onDragOverFolder) { e.preventDefault(); onDragOverFolder(); } }}
    onDrop={(e) => { if (onDropFolder) { e.preventDefault(); onDropFolder(); } }}
    className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl border text-center transition-colors cursor-default ${
      dropActive ? 'bg-amber-200 border-amber-400 ring-2 ring-amber-400'
        : selected ? 'bg-amber-100 border-amber-300 ring-2 ring-amber-400' : 'border-transparent hover:bg-amber-50 hover:border-amber-200'
    }`}
  >
    {/* price badge + settings button */}
    {onSettings && (
      <div className="absolute top-1 right-1 flex items-center gap-1">
        {price > 0 ? (
          <span className="text-[9px] font-bold bg-amber-600 text-white px-1.5 py-0.5 rounded-full">{price} د.أ</span>
        ) : (
          <span className="text-[9px] font-bold bg-green-600 text-white px-1.5 py-0.5 rounded-full">مجاني</span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onSettings(); }}
          className="p-1 rounded bg-white/80 hover:bg-amber-100 text-[#412402]/70 opacity-0 group-hover:opacity-100"
          title="إعدادات المجلد (السعر والصف والمحاولات)"
        >
          <Banknote className="w-3 h-3" />
        </button>
      </div>
    )}
    {/* rename button (top-left, like the videos folder) */}
    {onStartRename && !renaming && (
      <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100">
        <button
          onClick={(e) => { e.stopPropagation(); onStartRename(); }}
          className="p-1 rounded bg-white/80 hover:bg-amber-100 text-[#412402]/70"
          title="إعادة تسمية المجلد"
        >
          <Edit className="w-3 h-3" />
        </button>
      </div>
    )}
    <Win11FolderIcon size={64} />
    {renaming ? (
      <input
        autoFocus
        value={renameValue}
        onChange={(e) => onRenameChange(e.target.value)}
        onBlur={() => onRenameCommit(renameValue)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') { e.preventDefault(); onRenameCommit(renameValue); }
          else if (e.key === 'Escape') { e.preventDefault(); onRenameCancel(); }
        }}
        onClick={(e) => e.stopPropagation()}
        className="text-sm font-medium text-[#412402] text-center bg-white border border-amber-300 rounded px-1 py-0.5 outline-none max-w-[120px]"
      />
    ) : (
      <span className="text-sm font-medium text-[#412402] line-clamp-2 leading-tight max-w-[120px]">{name}</span>
    )}
    <span className="text-[11px] text-[#412402]/50">{count} عنصر</span>
  </div>
);

const Win11TopicTile = ({ topic, selected, onSelect, onOpen,
  draggable, onDragStart, onDragEnd, renaming, renameValue, onRenameChange, onRenameCommit, onRenameCancel, onStartRename,
  onMoveUp, onMoveDown, onDragOverReorder, onDropReorder, dropActive }) => (
  <div
    onClick={onSelect}
    onDoubleClick={() => { if (!renaming) onOpen(); }}
    draggable={draggable && !renaming}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onDragOver={onDragOverReorder}
    onDrop={onDropReorder}
    data-testid={`teacher-topic-${topic.id}`}
    className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl border text-center transition-colors cursor-default ${
      dropActive
        ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-400'
        : selected
        ? 'bg-amber-100 border-amber-300 ring-2 ring-amber-400'
        : 'border-transparent hover:bg-amber-50 hover:border-amber-200'
    }`}
  >
    {/* quick rename + reorder buttons */}
    {!renaming && (
      <div className="absolute top-1 left-1 flex gap-0.5 opacity-0 group-hover:opacity-100">
        {onStartRename && (
          <button onClick={(e) => { e.stopPropagation(); onStartRename(); }} className="p-1 rounded bg-white/80 hover:bg-amber-100 text-[#412402]/70" title="إعادة تسمية">
            <Edit className="w-3 h-3" />
          </button>
        )}
        {onMoveUp && (
          <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="p-1 rounded bg-white/80 hover:bg-amber-100 text-[#412402]/70" title="تحريك لليمين/أعلى">
            <ChevronUp className="w-3 h-3" />
          </button>
        )}
        {onMoveDown && (
          <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="p-1 rounded bg-white/80 hover:bg-amber-100 text-[#412402]/70" title="تحريك لليسار/أسفل">
            <ChevronDown className="w-3 h-3" />
          </button>
        )}
      </div>
    )}
    <div className="relative">
      <div className="w-16 h-20 rounded-lg bg-gradient-to-br from-white to-[#F4ECD8] border border-amber-200 shadow-sm flex flex-col items-center justify-center">
        <BookOpen className="w-7 h-7 text-amber-700" />
        <span className="text-[10px] text-amber-800 mt-1 font-bold">{topic.total_questions}</span>
      </div>
      {!topic.is_active && (
        <span className="absolute -top-1 -left-1 bg-gray-500 text-white text-[9px] px-1.5 py-0.5 rounded-full">معطّل</span>
      )}
    </div>
    {renaming ? (
      <input
        autoFocus
        value={renameValue}
        onChange={(e) => onRenameChange(e.target.value)}
        onBlur={() => onRenameCommit(renameValue)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') { e.preventDefault(); onRenameCommit(renameValue); }
          else if (e.key === 'Escape') { e.preventDefault(); onRenameCancel(); }
        }}
        onClick={(e) => e.stopPropagation()}
        className="text-sm font-medium text-[#412402] text-center bg-white border border-amber-300 rounded px-1 py-0.5 outline-none max-w-[140px]"
      />
    ) : (
      <span className="text-sm font-medium text-[#412402] line-clamp-2 leading-tight max-w-[140px]">{topic.title}</span>
    )}
    <span className="text-[11px] text-[#412402]/50 flex items-center gap-1">
      <Clock className="w-3 h-3" /> {topic.duration_minutes} د
    </span>
  </div>
);

const Win11ListRow = ({ icon, name, type, meta, selected, onSelect, onOpen }) => (
  <div
    onClick={onSelect}
    onDoubleClick={onOpen}
    className={`grid grid-cols-[24px_1fr_120px_120px] items-center gap-3 px-3 py-1.5 text-sm cursor-default ${
      selected ? 'bg-amber-100' : 'hover:bg-amber-50'
    }`}
  >
    <div className="flex items-center justify-center">{icon}</div>
    <div className="text-[#412402] truncate">{name}</div>
    <div className="text-[#412402]/60 text-xs">{type}</div>
    <div className="text-[#412402]/60 text-xs">{meta}</div>
  </div>
);

const Win11StatusBar = ({ totalItems, selectedTopic }) => (
  <div className="flex items-center justify-between bg-white/70 backdrop-blur border border-[#E5E5E5] rounded-xl px-3 py-1.5 text-xs text-[#412402]/70">
    <span>{totalItems} عنصر</span>
    {selectedTopic ? <span className="truncate">عنصر مُحدد: {selectedTopic.title}</span> : <span>لم يُحدد عنصر</span>}
  </div>
);

const TeacherQuestionBank = ({ topics, attempts = [], apiUrl, token, onCreateClick, onCreateInFolder, onManage, onDelete, onRefresh }) => {
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterQuestionText, setFilterQuestionText] = useState('');
  const [filterStudent, setFilterStudent] = useState('all');
  const [filterResult, setFilterResult] = useState('all');

  const studentNames = React.useMemo(() => {
    const set = new Set();
    (attempts || []).forEach(a => { if (a.student_name) set.add(a.student_name); });
    return Array.from(set).sort();
  }, [attempts]);

  const filteredTopicIds = React.useMemo(() => {
    const filtersActive = !!filterQuestionText.trim() || filterStudent !== 'all' || filterResult !== 'all';
    if (!filtersActive) return null;
    let pool = (topics || []).map(t => t.id);
    if (filterQuestionText.trim()) {
      const q = filterQuestionText.trim();
      pool = pool.filter(tid => {
        const t = topics.find(x => x.id === tid);
        return t && (t.questions || []).some(qq => (qq.text || '').includes(q));
      });
    }
    if (filterStudent !== 'all' || filterResult !== 'all') {
      const matchAttempt = (a) => {
        if (filterStudent !== 'all' && a.student_name !== filterStudent) return false;
        if (filterResult !== 'all') {
          const pct = a.total_points ? (a.score / a.total_points) * 100 : 0;
          if (filterResult === 'passed' && pct < 50) return false;
          if (filterResult === 'failed' && pct >= 50) return false;
          if (filterResult === 'in_progress' && a.status !== 'in_progress') return false;
          if (filterResult === 'completed' && a.status !== 'completed') return false;
          if (filterResult === 'terminated' && a.status !== 'terminated') return false;
        }
        return true;
      };
      const matchedTopicIds = new Set((attempts || []).filter(matchAttempt).map(a => a.topic_id));
      pool = pool.filter(tid => matchedTopicIds.has(tid));
    }
    return new Set(pool);
  }, [topics, attempts, filterQuestionText, filterStudent, filterResult]);

  const [emptyFolders, setEmptyFolders] = useState([]); // array of path strings
  const [folderSettingsMap, setFolderSettingsMap] = useState({}); // category -> {price, old_questions_max_attempts}

  const authHeaders = React.useMemo(
    () => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }),
    [token]
  );

  const refreshFolders = React.useCallback(async () => {
    if (!apiUrl) return;
    try {
      const res = await fetch(`${apiUrl}/api/topic-folders`, { headers: authHeaders });
      if (res.ok) setEmptyFolders((await res.json()).map((f) => f.path));
    } catch {/* ignore */}
    try {
      const res2 = await fetch(`${apiUrl}/api/teacher/folder-settings`, { headers: authHeaders });
      if (res2.ok) {
        const arr = await res2.json();
        const m = {};
        (arr || []).forEach((s) => { m[s.category] = s; });
        setFolderSettingsMap(m);
      }
    } catch {/* ignore */}
  }, [apiUrl, authHeaders]);

  useEffect(() => { refreshFolders(); }, [refreshFolders]);

  const apiCreateFolder = async (path) => {
    try {
      const res = await fetch(`${apiUrl}/api/topic-folders`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ path }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'فشل إنشاء المجلد');
        return false;
      }
      await refreshFolders();
      return true;
    } catch { toast.error('فشل الاتصال بالخادم'); return false; }
  };

  const apiDeleteFolder = async (path) => {
    try {
      const res = await fetch(`${apiUrl}/api/topic-folders?path=${encodeURIComponent(path)}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'فشل حذف المجلد');
        return false;
      }
      await refreshFolders();
      return true;
    } catch { toast.error('فشل الاتصال بالخادم'); return false; }
  };

  const apiRenameFolder = async (oldPath, newPath) => {
    try {
      const res = await fetch(`${apiUrl}/api/topic-folders/rename`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify({ old_path: oldPath, new_path: newPath }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'فشل إعادة التسمية');
        return false;
      }
      await refreshFolders();
      return true;
    } catch { toast.error('فشل الاتصال بالخادم'); return false; }
  };

  const tree = React.useMemo(() => {
    const root = buildTopicTree(topics);
    for (const path of emptyFolders) {
      const parts = path.split('/').map((p) => p.trim()).filter(Boolean);
      if (parts.length) ensureFolderPath(root, parts);
    }
    return root;
  }, [topics, emptyFolders]);

  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [renamingFolder, setRenamingFolder] = useState(null); // path string being renamed
  const [renamingFolderIsNew, setRenamingFolderIsNew] = useState(false); // true = cancel deletes the empty new folder
  const [renameValue, setRenameValue] = useState('');
  const [moveDialogTopic, setMoveDialogTopic] = useState(null);
  const [moveTargetPath, setMoveTargetPath] = useState('');
  const [movingTopic, setMovingTopic] = useState(false);
  const [moveFolderDialog, setMoveFolderDialog] = useState(null); // full path of folder to move
  const [moveFolderTarget, setMoveFolderTarget] = useState('');
  const [movingFolder, setMovingFolder] = useState(false);
  const [folderDeleteConfirm, setFolderDeleteConfirm] = useState(null); // folder name pending delete

  const allFolderPaths = React.useMemo(() => {
    const paths = new Set(['']);
    const walk = (node, parts) => {
      Object.entries(node.children || {}).forEach(([name, child]) => {
        const next = [...parts, name];
        paths.add(next.join('/'));
        walk(child, next);
      });
    };
    walk(tree, []);
    return Array.from(paths);
  }, [tree]);

  const submitMoveTopic = async () => {
    if (!moveDialogTopic) return;
    setMovingTopic(true);
    try {
      const res = await fetch(`${apiUrl}/api/topics/${moveDialogTopic.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ category: moveTargetPath === '__root__' ? '' : (moveTargetPath || '') }),
      });
      if (res.ok) {
        toast.success('تم نقل الموضوع');
        moveDialogTopic.category = moveTargetPath === '__root__' ? '' : (moveTargetPath || '');
        setMoveDialogTopic(null);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'فشل النقل');
      }
    } catch {
      toast.error('فشل الاتصال بالخادم');
    }
    setMovingTopic(false);
  };

  const submitMoveFolder = async () => {
    if (!moveFolderDialog) return;
    const folderName = moveFolderDialog.split('/').pop();
    const targetParent = moveFolderTarget === '__root__' ? '' : moveFolderTarget;
    const newPath = targetParent ? `${targetParent}/${folderName}` : folderName;
    if (newPath === moveFolderDialog) { setMoveFolderDialog(null); return; }
    setMovingFolder(true);
    const ok = await apiRenameFolder(moveFolderDialog, newPath);
    setMovingFolder(false);
    if (ok) {
      toast.success('تم نقل المجلد');
      setMoveFolderDialog(null);
      setSelectedFolder(null);
    }
  };

  const currentPath = history[historyIndex];
  const currentNode = getNodeAtPath(tree, currentPath) || { children: {}, topics: [] };

  const navigate = (parts) => {
    const sliced = history.slice(0, historyIndex + 1);
    sliced.push(parts);
    setHistory(sliced);
    setHistoryIndex(sliced.length - 1);
    setSelectedTopicId(null);
    setSearch('');
  };
  const goBack = () => { if (historyIndex > 0) { setHistoryIndex(historyIndex - 1); setSelectedTopicId(null); } };
  const goForward = () => { if (historyIndex < history.length - 1) { setHistoryIndex(historyIndex + 1); setSelectedTopicId(null); } };
  const goUp = () => { if (currentPath.length > 0) navigate(currentPath.slice(0, -1)); };

  const folderEntries = Object.keys(currentNode.children)
    .sort((a, b) => a.localeCompare(b, 'ar'))
    .filter((name) => !search || name.includes(search))
    .map((name) => ({
      name,
      count: countTreeTopics(currentNode.children[name]),
    }));

  const topicEntries = (currentNode.topics || [])
    .filter((t) => !search
      || (t.title || '').includes(search)
      || (t.description || '').includes(search)
      || (t.questions || []).some((q) => (q.text || '').includes(search)))
    .filter((t) => !filteredTopicIds || filteredTopicIds.has(t.id));

  const selectedTopic = topicEntries.find((t) => t.id === selectedTopicId) || null;
  const currentPathStr = currentPath.join('/');

  // Folder-level pricing + old-question attempt limit (the whole folder is the
  // unit the student buys/locks).
  const [folderSettingsOpen, setFolderSettingsOpen] = useState(false);
  const [folderSettingsCategory, setFolderSettingsCategory] = useState('');
  // tries: '' = unlimited (بلا حد), 0 = محاولة واحدة فقط ثم يُقفل المجلد, N = N محاولات.
  const [folderSettingsForm, setFolderSettingsForm] = useState({
    price: 0, tries: '', grade: '', section: '',
    questions_per_test: 10, easy_count: 3, medium_count: 4, hard_count: 3,
    duration_minutes: 30, max_violations: 2, passing_percentage: 50,
    show_correct_answers: true, show_score_to_student: true,
  });
  const [savingFolderSettings, setSavingFolderSettings] = useState(false);

  const openFolderSettings = (category = currentPathStr) => {
    const found = folderSettingsMap[category];
    setFolderSettingsCategory(category);
    setFolderSettingsForm({
      price: found?.price ?? 0,
      tries: (found?.tries === null || found?.tries === undefined) ? '' : found.tries,
      grade: found?.grade ?? '',
      section: found?.section ?? '',
      questions_per_test: found?.questions_per_test ?? 10,
      easy_count: found?.easy_count ?? 3,
      medium_count: found?.medium_count ?? 4,
      hard_count: found?.hard_count ?? 3,
      duration_minutes: found?.duration_minutes ?? 30,
      max_violations: found?.max_violations ?? 2,
      passing_percentage: found?.passing_percentage ?? 50,
      show_correct_answers: found?.show_correct_answers ?? true,
      show_score_to_student: found?.show_score_to_student ?? true,
    });
    setFolderSettingsOpen(true);
  };

  const saveFolderSettings = async () => {
    setSavingFolderSettings(true);
    try {
      const f = folderSettingsForm;
      const triesRaw = (f.tries === '' || f.tries === null || f.tries === undefined)
        ? null                                   // بلا حد
        : Math.max(0, parseInt(f.tries, 10) || 0);
      const res = await fetch(`${apiUrl}/api/teacher/folder-settings`, {
        method: 'PUT', headers: authHeaders,
        body: JSON.stringify({
          category: folderSettingsCategory,
          price: Math.max(0, Number(f.price) || 0),
          tries: triesRaw,
          grade: f.grade || '',
          section: f.section || '',
          questions_per_test: Math.max(0, parseInt(f.questions_per_test || 0, 10)),
          easy_count: Math.max(0, parseInt(f.easy_count || 0, 10)),
          medium_count: Math.max(0, parseInt(f.medium_count || 0, 10)),
          hard_count: Math.max(0, parseInt(f.hard_count || 0, 10)),
          duration_minutes: Math.max(0, parseInt(f.duration_minutes || 0, 10)),
          max_violations: Math.max(0, parseInt(f.max_violations || 0, 10)),
          passing_percentage: Math.max(0, parseInt(f.passing_percentage || 0, 10)),
          show_correct_answers: !!f.show_correct_answers,
          show_score_to_student: !!f.show_score_to_student,
        }),
      });
      if (res.ok) {
        toast.success('تم حفظ إعدادات المجلد');
        setFolderSettingsOpen(false);
        await refreshFolders();
        onRefresh?.(); // grade/section cascade to files → refresh topics
      }
      else toast.error('فشل حفظ الإعدادات');
    } catch { toast.error('فشل الاتصال بالخادم'); }
    setSavingFolderSettings(false);
  };

  const commitRename = async (oldPath, newName, wasNew = false) => {
    const trimmed = (newName || '').trim();
    if (!trimmed) {
      setRenamingFolder(null);
      // committing an empty name on a brand-new folder removes the placeholder
      if (wasNew) await deleteEmptyFolder(oldPath.split('/').pop());
      return;
    }
    const parentParts = oldPath.split('/').slice(0, -1);
    const newPath = [...parentParts, trimmed].join('/');
    if (newPath === oldPath) { setRenamingFolder(null); return; }
    const ok = await apiRenameFolder(oldPath, newPath);
    setRenamingFolder(null);
    if (ok) setSelectedFolder(trimmed);
  };

  const deleteEmptyFolder = async (folderName) => {
    const fullPath = currentPathStr ? `${currentPathStr}/${folderName}` : folderName;
    const ok = await apiDeleteFolder(fullPath);
    if (ok) setSelectedFolder(null);
  };

  // ── Drag & drop + inline topic rename ──────────────────────────────────────
  const [dragItem, setDragItem] = useState(null); // { kind:'topic'|'folder', id?, name?, path? }
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [dragOverTopicId, setDragOverTopicId] = useState(null); // reorder target tile
  const [renamingTopicId, setRenamingTopicId] = useState(null);
  const [topicRenameValue, setTopicRenameValue] = useState('');

  const moveTopicToPath = async (topic, category) => {
    if ((topic.category || '') === (category || '')) return;
    try {
      const res = await fetch(`${apiUrl}/api/topics/${topic.id}`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify({ category: category || '' }),
      });
      if (res.ok) { toast.success('تم نقل الموضوع'); onRefresh?.(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'فشل النقل'); }
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const moveFolderToParent = async (folderFullPath, targetParent) => {
    const folderName = folderFullPath.split('/').pop();
    // prevent dropping a folder into itself or its descendant
    if (targetParent === folderFullPath || targetParent.startsWith(folderFullPath + '/')) {
      toast.error('لا يمكن نقل المجلد داخل نفسه'); return;
    }
    const newPath = targetParent ? `${targetParent}/${folderName}` : folderName;
    if (newPath === folderFullPath) return;
    const ok = await apiRenameFolder(folderFullPath, newPath);
    if (ok) { toast.success('تم نقل المجلد'); onRefresh?.(); }
  };

  const handleDropOnFolder = (folderName) => {
    const targetPath = currentPathStr ? `${currentPathStr}/${folderName}` : folderName;
    if (dragItem?.kind === 'topic') moveTopicToPath(dragItem.topic, targetPath);
    else if (dragItem?.kind === 'folder') moveFolderToParent(dragItem.path, targetPath);
    setDragItem(null); setDragOverFolder(null);
  };

  const handleDropOnPath = (targetPath) => {
    // drop onto a breadcrumb segment ('' = root) to move OUT to that level
    if (dragItem?.kind === 'topic') moveTopicToPath(dragItem.topic, targetPath);
    else if (dragItem?.kind === 'folder') moveFolderToParent(dragItem.path, targetPath);
    setDragItem(null); setDragOverFolder(null);
  };

  const persistTopicOrder = async (ids) => {
    // optimistic: assign order locally so the UI doesn't jump before refresh
    ids.forEach((id, idx) => {
      const t = topics.find((x) => x.id === id);
      if (t) t.order = idx;
    });
    try {
      await fetch(`${apiUrl}/api/topics/reorder`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ ordered_ids: ids }),
      });
      onRefresh?.();
    } catch { toast.error('فشل حفظ الترتيب'); }
  };

  const reorderTopicsInFolder = async (topic, dir) => {
    const ids = topicEntries.map((t) => t.id);
    const i = ids.indexOf(topic.id);
    const j = i + dir;
    if (i === -1 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await persistTopicOrder(ids);
  };

  // Drag a topic tile onto another topic in the SAME folder to reorder (like videos).
  const reorderTopicsByDrop = async (targetId) => {
    setDragOverTopicId(null);
    if (dragItem?.kind !== 'topic') return;
    const dragId = dragItem.id;
    if (!dragId || dragId === targetId) return;
    const ids = topicEntries.map((t) => t.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return; // different folder → handled as a move, not reorder
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    await persistTopicOrder(ids);
  };

  const renameTopic = async (topic, newTitle) => {
    const trimmed = (newTitle || '').trim();
    setRenamingTopicId(null);
    if (!trimmed || trimmed === topic.title) return;
    try {
      const res = await fetch(`${apiUrl}/api/topics/${topic.id}`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify({ title: trimmed }),
      });
      if (res.ok) { toast.success('تم تغيير الاسم'); onRefresh?.(); }
      else toast.error('فشل تغيير الاسم');
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  return (
    <div className="space-y-3 bg-gradient-to-br from-[#FAF6EC] to-[#F1E8D2] rounded-2xl p-3 md:p-4 border border-[#E8DFCF]">
      {/* Title bar */}
      <div className="flex items-center justify-between px-2 pt-1">
        <div className="flex items-center gap-2 text-[#412402]">
          <FolderOpen className="w-5 h-5 text-amber-700" />
          <span className="font-bold">بنك الأسئلة</span>
          <span className="text-xs text-[#412402]/50">— مستكشف المجلدات</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openFolderSettings(currentPathStr)}
            data-testid="folder-settings-btn"
            title="تحديد سعر فتح هذا المجلد وعدد محاولات الأسئلة القديمة"
          >
            <Banknote className="w-4 h-4 ml-1" />
            سعر المجلد الحالي
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilterPanel(v => !v)}
            data-testid="bank-filter-toggle-btn"
          >
            <Filter className="w-4 h-4 ml-1" />
            فلترة
          </Button>
          <div className="text-xs text-[#412402]/50">{topics.length} موضوع إجمالاً</div>
        </div>
      </div>

      {showFilterPanel && (
        <div className="grid sm:grid-cols-3 gap-3 p-3 bg-white border border-[#E5E5E5] rounded-xl">
          <div className="space-y-1">
            <Label className="text-xs text-[#412402]/70">بحث في نصوص الأسئلة</Label>
            <Input
              value={filterQuestionText}
              onChange={(e) => setFilterQuestionText(e.target.value)}
              placeholder="أدخل نص السؤال..."
              data-testid="bank-filter-question"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#412402]/70">حسب الطالب</Label>
            <Select value={filterStudent} onValueChange={setFilterStudent}>
              <SelectTrigger data-testid="bank-filter-student"><SelectValue placeholder="جميع الطلاب" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الطلاب</SelectItem>
                {studentNames.map(n => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#412402]/70">حسب النتائج</Label>
            <Select value={filterResult} onValueChange={setFilterResult}>
              <SelectTrigger data-testid="bank-filter-result"><SelectValue placeholder="كل الحالات" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الحالات</SelectItem>
                <SelectItem value="passed">ناجح (≥ 50%)</SelectItem>
                <SelectItem value="failed">راسب (&lt; 50%)</SelectItem>
                <SelectItem value="completed">مكتمل</SelectItem>
                <SelectItem value="in_progress">قيد التنفيذ</SelectItem>
                <SelectItem value="terminated">مُنهَى (مخالفات)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-3 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFilterQuestionText(''); setFilterStudent('all'); setFilterResult('all'); }}
            >
              إعادة ضبط الفلاتر
            </Button>
          </div>
        </div>
      )}

      <Win11AddressBar
        pathParts={currentPath}
        onNavigate={navigate}
        onBack={goBack}
        onForward={goForward}
        onUp={goUp}
        canBack={historyIndex > 0}
        canForward={historyIndex < history.length - 1}
        canUp={currentPath.length > 0}
        search={search}
        onSearchChange={setSearch}
        onDropPath={dragItem ? handleDropOnPath : undefined}
      />

      <Win11Toolbar
        onMove={(topic) => { setMoveDialogTopic(topic); setMoveTargetPath(topic.category || '__root__'); }}
        onMoveFolder={(folderName) => {
          const fullPath = currentPathStr ? `${currentPathStr}/${folderName}` : folderName;
          setMoveFolderDialog(fullPath);
          setMoveFolderTarget(currentPathStr || '__root__');
        }}
        onCreateTopic={() => (currentPathStr ? onCreateInFolder?.(currentPathStr) : onCreateClick())}
        onCreateFolder={async () => {
          const existing = new Set(Object.keys(currentNode.children));
          let base = 'مجلد جديد';
          let name = base;
          let i = 2;
          while (existing.has(name)) { name = `${base} (${i++})`; }
          const newPath = currentPathStr ? `${currentPathStr}/${name}` : name;
          const ok = await apiCreateFolder(newPath);
          if (!ok) return;
          setRenamingFolder(newPath);
          setRenamingFolderIsNew(true);
          setRenameValue(name);
          setSelectedFolder(name);
          setSelectedTopicId(null);
        }}
        view={view}
        onViewChange={setView}
        selectedTopic={selectedTopic}
        onManage={onManage}
        onDelete={(t) => {
          if (t) onDelete(t);
          else if (selectedFolder) {
            const fullPath = currentPathStr ? `${currentPathStr}/${selectedFolder}` : selectedFolder;
            const node = getNodeAtPath(tree, fullPath.split('/'));
            const childCount = node ? countTreeTopics(node) + Object.keys(node.children).length : 0;
            if (childCount > 0) {
              toast.error('المجلد غير فارغ — لا يمكن حذفه');
              return;
            }
            setFolderDeleteConfirm(selectedFolder);
          }
        }}
        selectedFolderName={selectedFolder}
      />

      <div className="flex gap-3">
        <Win11Sidebar tree={tree} currentPath={currentPath} onNavigate={navigate} />

        <div className="flex-1 bg-white/80 backdrop-blur border border-[#E5E5E5] rounded-xl min-h-[400px] p-3"
             onClick={(e) => { if (e.target === e.currentTarget) { setSelectedTopicId(null); setSelectedFolder(null); } }}>
          {topics.length === 0 && folderEntries.length === 0 ? (
            <div className="py-16 text-center">
              <Win11FolderIcon size={72} />
              <h3 className="mt-3 text-lg font-semibold text-[#412402]/70">هذا المجلد فارغ</h3>
              <p className="text-[#412402]/50 mb-4 text-sm">أنشئ موضوعاً لبدء بنك الأسئلة</p>
              <Button onClick={onCreateClick} className="bg-amber-700 hover:bg-amber-800" data-testid="create-topic-btn">
                <Plus className="w-4 h-4 ml-1" /> إنشاء موضوع
              </Button>
            </div>
          ) : folderEntries.length === 0 && topicEntries.length === 0 ? (
            <div className="py-16 text-center text-[#412402]/50 text-sm">
              {search ? 'لا توجد نتائج مطابقة للبحث' : 'هذا المجلد فارغ'}
            </div>
          ) : view === 'grid' ? (
            <div className="space-y-4">
              {folderEntries.length > 0 && (
                <div>
                  <div className="px-2 mb-1 text-[11px] font-semibold text-[#412402]/60">مجلدات</div>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-1">
                    {folderEntries.map((f) => {
                      const fullPath = currentPathStr ? `${currentPathStr}/${f.name}` : f.name;
                      const isRenaming = renamingFolder === fullPath;
                      return (
                        <Win11FolderTile
                          key={f.name}
                          name={f.name}
                          count={f.count}
                          selected={selectedFolder === f.name}
                          renaming={isRenaming}
                          renameValue={renameValue}
                          onRenameChange={setRenameValue}
                          onRenameCommit={(v) => { const wasNew = renamingFolderIsNew; setRenamingFolderIsNew(false); commitRename(fullPath, v, wasNew); }}
                          onRenameCancel={() => { const wasNew = renamingFolderIsNew; setRenamingFolder(null); setRenamingFolderIsNew(false); if (wasNew) deleteEmptyFolder(f.name); }}
                          onStartRename={() => { setRenamingFolder(fullPath); setRenamingFolderIsNew(false); setRenameValue(f.name); }}
                          onSelect={() => { setSelectedFolder(f.name); setSelectedTopicId(null); }}
                          onOpen={() => navigate([...currentPath, f.name])}
                          draggable
                          onDragStart={() => setDragItem({ kind: 'folder', name: f.name, path: fullPath })}
                          onDragEnd={() => { setDragItem(null); setDragOverFolder(null); }}
                          onDragOverFolder={() => { if (dragItem && !(dragItem.kind === 'folder' && dragItem.path === fullPath)) setDragOverFolder(f.name); }}
                          onDropFolder={() => handleDropOnFolder(f.name)}
                          dropActive={dragOverFolder === f.name}
                          price={folderSettingsMap[fullPath]?.price || 0}
                          onSettings={() => openFolderSettings(fullPath)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              {topicEntries.length > 0 && (
                <div>
                  <div className="px-2 mb-1 text-[11px] font-semibold text-[#412402]/60">المواضيع</div>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-1">
                    {topicEntries.map((t) => (
                      <Win11TopicTile
                        key={t.id}
                        topic={t}
                        selected={selectedTopicId === t.id}
                        onSelect={() => setSelectedTopicId(t.id)}
                        onOpen={() => onManage(t)}
                        draggable
                        onDragStart={() => setDragItem({ kind: 'topic', id: t.id, topic: t })}
                        onDragEnd={() => { setDragItem(null); setDragOverFolder(null); setDragOverTopicId(null); }}
                        onDragOverReorder={(e) => {
                          if (dragItem?.kind === 'topic' && dragItem.id !== t.id) { e.preventDefault(); setDragOverTopicId(t.id); }
                        }}
                        onDropReorder={(e) => { e.preventDefault(); e.stopPropagation(); reorderTopicsByDrop(t.id); }}
                        dropActive={dragOverTopicId === t.id}
                        renaming={renamingTopicId === t.id}
                        renameValue={topicRenameValue}
                        onRenameChange={setTopicRenameValue}
                        onRenameCommit={(v) => renameTopic(t, v)}
                        onRenameCancel={() => setRenamingTopicId(null)}
                        onStartRename={() => { setRenamingTopicId(t.id); setTopicRenameValue(t.title); }}
                        onMoveUp={() => reorderTopicsInFolder(t, -1)}
                        onMoveDown={() => reorderTopicsInFolder(t, 1)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-[24px_1fr_120px_120px] gap-3 px-3 py-1.5 text-[11px] font-semibold text-[#412402]/60 border-b border-[#E5E5E5]">
                <div></div>
                <div>الاسم</div>
                <div>النوع</div>
                <div>التفاصيل</div>
              </div>
              {folderEntries.map((f) => (
                <Win11ListRow
                  key={`f-${f.name}`}
                  icon={<Folder className="w-4 h-4 text-amber-600" />}
                  name={f.name}
                  type="مجلد"
                  meta={`${f.count} عنصر`}
                  selected={selectedFolder === f.name}
                  onSelect={() => { setSelectedFolder(f.name); setSelectedTopicId(null); }}
                  onOpen={() => navigate([...currentPath, f.name])}
                />
              ))}
              {topicEntries.map((t) => (
                <Win11ListRow
                  key={`t-${t.id}`}
                  icon={<BookOpen className="w-4 h-4 text-amber-700" />}
                  name={t.title}
                  type="موضوع"
                  meta={`${t.total_questions} سؤال • ${t.duration_minutes} د`}
                  selected={selectedTopicId === t.id}
                  onSelect={() => setSelectedTopicId(t.id)}
                  onOpen={() => onManage(t)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Win11StatusBar
        totalItems={folderEntries.length + topicEntries.length}
        selectedTopic={selectedTopic}
      />

      {folderDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle className="text-base">تأكيد حذف المجلد</CardTitle>
              <CardDescription className="text-xs">
                هل أنت متأكد من حذف المجلد "{folderDeleteConfirm}"؟ لا يمكن التراجع.
              </CardDescription>
            </CardHeader>
            <div className="p-4 border-t flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setFolderDeleteConfirm(null)}>إلغاء</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={async () => {
                  await deleteEmptyFolder(folderDeleteConfirm);
                  setFolderDeleteConfirm(null);
                }}
              >
                حذف
              </Button>
            </div>
          </Card>
        </div>
      )}

      {moveFolderDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="border-b">
              <CardTitle className="text-base">نقل المجلد إلى موقع آخر</CardTitle>
              <CardDescription className="text-xs">{moveFolderDialog}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 py-4">
              <div className="space-y-1">
                <Label className="text-sm">المجلد المستهدف (المكان الجديد)</Label>
                <Select value={moveFolderTarget} onValueChange={setMoveFolderTarget}>
                  <SelectTrigger><SelectValue placeholder="(الجذر)" /></SelectTrigger>
                  <SelectContent>
                    {allFolderPaths
                      .filter(p => p !== moveFolderDialog && !p.startsWith(moveFolderDialog + '/'))
                      .map(p => (
                        <SelectItem key={p || '__root__'} value={p || '__root__'}>{p === '' ? '(الجذر)' : p}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#412402]/60">يمكنك أيضاً كتابة مسار مجلد آخر:</p>
                <Input
                  value={moveFolderTarget}
                  onChange={(e) => setMoveFolderTarget(e.target.value)}
                  placeholder="مثال: رياضيات/الفصل الأول"
                />
              </div>
            </CardContent>
            <div className="p-4 border-t flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setMoveFolderDialog(null)} disabled={movingFolder}>إلغاء</Button>
              <Button onClick={submitMoveFolder} disabled={movingFolder} className="bg-amber-700 hover:bg-amber-800">
                {movingFolder ? 'جاري النقل...' : 'نقل'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {moveDialogTopic && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="border-b">
              <CardTitle className="text-base">نقل الموضوع إلى مجلد آخر</CardTitle>
              <CardDescription className="text-xs">{moveDialogTopic.title}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 py-4">
              <div className="space-y-1">
                <Label className="text-sm">المجلد المستهدف</Label>
                <Select value={moveTargetPath} onValueChange={setMoveTargetPath}>
                  <SelectTrigger><SelectValue placeholder="(الجذر)" /></SelectTrigger>
                  <SelectContent>
                    {allFolderPaths.map(p => (
                      <SelectItem key={p || '__root__'} value={p || '__root__'}>{p === '' ? '(الجذر)' : p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#412402]/60">يمكنك أيضاً كتابة مسار مجلد جديد:</p>
                <Input
                  value={moveTargetPath}
                  onChange={(e) => setMoveTargetPath(e.target.value)}
                  placeholder="مثال: رياضيات/الفصل الأول"
                />
              </div>
            </CardContent>
            <div className="p-4 border-t flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setMoveDialogTopic(null)} disabled={movingTopic}>إلغاء</Button>
              <Button onClick={submitMoveTopic} disabled={movingTopic} className="bg-amber-700 hover:bg-amber-800">
                {movingTopic ? 'جاري النقل...' : 'نقل'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Folder settings: the folder owns grade/section, the exam config, price and tries */}
      <Dialog open={folderSettingsOpen} onOpenChange={setFolderSettingsOpen}>
        <DialogContent dir="rtl" className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-amber-700" /> إعدادات المجلد
            </DialogTitle>
            <DialogDescription>
              {folderSettingsCategory ? `المجلد: ${folderSettingsCategory}` : 'المجلد الرئيسي (الجذر)'} — يشتري الطالب المجلد كاملاً، وهذه الإعدادات تُطبَّق على كل ملفات المجلد.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>الصف (لكل المجلد)</Label>
                <Select
                  value={folderSettingsForm.grade || '__none__'}
                  onValueChange={(v) => setFolderSettingsForm({ ...folderSettingsForm, grade: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger data-testid="folder-grade-select"><SelectValue placeholder="بدون تحديد" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">بدون تحديد</SelectItem>
                    {FOLDER_GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>الشعبة (اختياري)</Label>
                <Select
                  value={folderSettingsForm.section || '__all__'}
                  onValueChange={(v) => setFolderSettingsForm({ ...folderSettingsForm, section: v === '__all__' ? '' : v })}
                >
                  <SelectTrigger data-testid="folder-section-select"><SelectValue placeholder="جميع الشعب" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">جميع الشعب</SelectItem>
                    {FOLDER_SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-[#412402]/50 -mt-1">يُطبَّق الصف/الشعبة على كل الملفات داخل المجلد تلقائياً.</p>

            <div className="grid grid-cols-2 gap-3 border-t pt-3">
              <div>
                <Label>مدة الاختبار (دقيقة)</Label>
                <Input
                  type="number" min={1} step="1"
                  value={folderSettingsForm.duration_minutes}
                  onChange={(e) => setFolderSettingsForm({ ...folderSettingsForm, duration_minutes: Math.max(0, parseInt(e.target.value || '0', 10)) })}
                  data-testid="folder-duration-input"
                />
              </div>
              <div>
                <Label>عدد الأسئلة في كل اختبار</Label>
                <Input
                  type="number" min={1} step="1"
                  value={folderSettingsForm.questions_per_test}
                  onChange={(e) => setFolderSettingsForm({ ...folderSettingsForm, questions_per_test: Math.max(0, parseInt(e.target.value || '0', 10)) })}
                  data-testid="folder-qpt-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">توزيع الصعوبة في كل اختبار</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <div>
                  <Label className="text-xs">سهلة</Label>
                  <Input type="number" min={0} value={folderSettingsForm.easy_count}
                    onChange={(e) => setFolderSettingsForm({ ...folderSettingsForm, easy_count: Math.max(0, parseInt(e.target.value || '0', 10)) })} />
                </div>
                <div>
                  <Label className="text-xs">متوسطة</Label>
                  <Input type="number" min={0} value={folderSettingsForm.medium_count}
                    onChange={(e) => setFolderSettingsForm({ ...folderSettingsForm, medium_count: Math.max(0, parseInt(e.target.value || '0', 10)) })} />
                </div>
                <div>
                  <Label className="text-xs">صعبة</Label>
                  <Input type="number" min={0} value={folderSettingsForm.hard_count}
                    onChange={(e) => setFolderSettingsForm({ ...folderSettingsForm, hard_count: Math.max(0, parseInt(e.target.value || '0', 10)) })} />
                </div>
              </div>
              <p className="text-[11px] text-[#412402]/50 mt-1">
                المجموع: {(folderSettingsForm.easy_count || 0) + (folderSettingsForm.medium_count || 0) + (folderSettingsForm.hard_count || 0)} سؤال
              </p>
            </div>

            <div className="border-t pt-3">
              <Label>عدد المحاولات (لكل ملف)</Label>
              <Input
                type="number" min={0} step="1"
                value={folderSettingsForm.tries}
                placeholder="اتركه فارغاً = بلا حد"
                onChange={(e) => setFolderSettingsForm({ ...folderSettingsForm, tries: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value || '0', 10)) })}
                data-testid="folder-tries-input"
              />
              <p className="text-[11px] text-[#412402]/50 mt-1">
                يُطبَّق هذا العدد على كل ملف (بنك أسئلة) داخل المجلد. عند انتهاء محاولات ملف يُقفل ذلك الملف وتبقى بقية الملفات متاحة، وعند انتهاء محاولات كل الملفات يُقفل المجلد كاملاً ولا يُفتح إلا بإعادة شرائه بنفس السعر.
                <b> اتركه فارغاً = بلا حد</b>، <b>0 = محاولة واحدة فقط لكل ملف ثم يُقفل الملف</b>.
              </p>
            </div>
            <div>
              <Label>سعر فتح المجلد (د.أ)</Label>
              <Input
                type="number" min={0} step="1"
                value={folderSettingsForm.price}
                onChange={(e) => setFolderSettingsForm({ ...folderSettingsForm, price: Math.max(0, Number(e.target.value) || 0) })}
                data-testid="folder-price-input"
              />
              <p className="text-[11px] text-[#412402]/50 mt-1">يُخصم من رصيد الطالب مرة لفتح كل مواضيع المجلد (0 = مجاني). لا يتغير عند حذف ملفات المجلد.</p>
            </div>

            <details className="text-[12px]">
              <summary className="cursor-pointer text-[#412402]/60">إعدادات متقدمة</summary>
              <div className="mt-2 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">الحد الأقصى للمخالفات</Label>
                    <Input type="number" min={0} value={folderSettingsForm.max_violations}
                      onChange={(e) => setFolderSettingsForm({ ...folderSettingsForm, max_violations: Math.max(0, parseInt(e.target.value || '0', 10)) })} />
                  </div>
                  <div>
                    <Label className="text-xs">نسبة النجاح %</Label>
                    <Input type="number" min={0} max={100} value={folderSettingsForm.passing_percentage}
                      onChange={(e) => setFolderSettingsForm({ ...folderSettingsForm, passing_percentage: Math.max(0, parseInt(e.target.value || '0', 10)) })} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">إظهار الإجابات الصحيحة بعد التسليم</Label>
                  <Switch checked={folderSettingsForm.show_correct_answers}
                    onCheckedChange={(v) => setFolderSettingsForm({ ...folderSettingsForm, show_correct_answers: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">إظهار الدرجة للطالب</Label>
                  <Switch checked={folderSettingsForm.show_score_to_student}
                    onCheckedChange={(v) => setFolderSettingsForm({ ...folderSettingsForm, show_score_to_student: v })} />
                </div>
              </div>
            </details>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderSettingsOpen(false)}>إلغاء</Button>
            <Button onClick={saveFolderSettings} disabled={savingFolderSettings} className="bg-amber-700 hover:bg-amber-800" data-testid="save-folder-settings-btn">
              {savingFolderSettings ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              حفظ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboard;
